// MOBILIERUL PLANȘEI TEHNICE — desen de linie, după modelul cerut.
//
// A DOUA bibliotecă de mobilier, nu o rescriere a celei existente.
// `arhMobilier` desenează piese cu ton, umbră și material, pentru planul
// RANDAT („2D"); aici sunt aceleași funcțiuni desenate ca pe o planșă: contur
// pe hârtie, fără umplere colorată, cu piesa recunoscută după FORMĂ.
//
// Două biblioteci fiindcă cele două desene spun lucruri diferite. Pe planul
// randat, o canapea e un corp moale care ocupă loc — de aia are ton. Pe planșă,
// aceeași canapea e un simbol: spune „aici se stă", și trebuie citită din linie
// la orice mărire. Una și aceeași piesă „stinsă pe alb" ar fi fost un
// compromis între cele două, adică bună la niciuna — și chiar așa arăta.
//
// Piesele se desenează într-un cadru CENTRAT în origine, lat `w` și înalt `h`:
// cadrul camerei, măsurat și rotit de `arhMobilier.cadru()`, care rămâne al
// amândurora. Deci aici nu se știe nimic despre unde e camera pe planșă — doar
// cât de mare e și încotro e „sus".

import { createContext, useContext, type ReactNode } from "react";
import { t } from "../../i18n";
import type { Cheie } from "../../i18n";
import { BIBLIOTECA } from "../generated/biblioteca";
import { SIMBOLURI } from "./simboluriCad";
import { scauneleMesei } from "../tronson/design/masaCuScaune";
import { scaraDin } from "./scara";

/** Cerneala și hârtia mobilierului. Aceleași variabile ca în `arhMobilier`,
 *  deci planșa de noapte le întoarce pe amândouă dintr-un singur loc. */
const LINIE = "var(--mob-linie, #111111)";
const FOND = "var(--mob-corp, #ffffff)";

/** Grosimile, în pixeli constanți — ca pereții, cotele și hașura. Scrise în
 *  metri, s-ar îngroșa cu mărirea și piesele ar ieși mai grase decât zidul
 *  care le conține. */
const K = 55;
/** GROSIMEA UNEI LINII DE MOBILĂ, ca valoare CSS.
 *
 *  Cifra de bază e aceeași ca înainte — pixeli constanți, ca pereții și cotele.
 *  Ce s-a adăugat e `--mob-gros`, un FACTOR pe care îl pune cine desenează:
 *
 *    * paleta îl dă mic, fiindcă acolo simbolul e strâns într-un buton de 44 px
 *      și liniile de 1,2 px îl fac să se citească drept pată neagră (frigiderul
 *      și aragazul, mai ales — «să aibă linii mai subțiri, că arată ciudat»);
 *    * pânza îl scade la depărtare (v. `Desen::grosimeaMobilei`): la mărire
 *      mică piesa se strânge, dar linia rămâne de 1,2 px și ajunge mai groasă
 *      decât piesa pe care o desenează.
 *
 *  Nedat, factorul e 1 și nu se schimbă nimic. Merge prin `style` fiindcă
 *  `var()` într-un atribut de prezentare nu e citit peste tot. */
const g = (m: number) =>
  `calc(var(--mob-gros, 1) * ${Math.max(0.6, m * K)}px)`;

/** COASE UN DRUM RUPT ÎN SEGMENTE, ca să aibă ce umple.
 *
 *  Unele simboluri scoase din planșă vin ca segmente separate: fiecare bucată
 *  e propriul ei subdrum (`M a L b M b L c …`). Desenate cu linia arată
 *  întreg, dar UMPLUTE nu dau nimic — un subdrum de două puncte n-are arie,
 *  deci piesa rămânea străvezie (taburetul, comoda TV, măsuța de cafea). Cele
 *  trasate dintr-o singură curbă — masa rotundă — se umpleau de la sine, de-aia
 *  defectul arăta capricios.
 *
 *  Aici se lipesc la loc: un `M` care începe chiar unde s-a terminat bucata
 *  dinainte nu e un subdrum nou, e continuarea ei. Se coase NUMAI copia care
 *  se umple; desenul cu linia rămâne neatins, deci nicio piesă nu-și schimbă
 *  conturul. Coordonatele se compară ca text, fiindcă le-a scris același
 *  exportator în amândouă capetele — nicio rotunjire între ele. */
const NUMAR_DRUM = /-?\d*\.?\d+/g;
const capatulDrumului = (x: string): string | null => {
  const n = x.match(NUMAR_DRUM);
  return n && n.length >= 2 ? `${n[n.length - 2]},${n[n.length - 1]}` : null;
};
const inceputulDrumului = (x: string): string | null => {
  const n = x.slice(1).match(NUMAR_DRUM);
  return n && n.length >= 2 ? `${n[0]},${n[1]}` : null;
};
/** `M x,y` scos CU TOTUL — litera ȘI perechea lui de coordonate.
 *
 *  Tăiată numai litera (`slice(1)`), perechea rămânea lipită de comanda
 *  dinainte: un `C` care avea șase numere se trezea cu OPT, adică o cale pe
 *  care SVG-ul n-o mai poate citi. Umplerea cădea cu totul — iar conturul nu,
 *  fiindcă el se desenează din `d` BRUT, nu din cel cusut. De-aia măsuța de
 *  cafea se vedea desenată, dar prin ea răzbătea parchetul: elipsa ei e un
 *  singur drum cu 24 de curbe, din care 23 ajungeau stricate.
 *
 *  Punctul scos nu lipsește din desen: el e, prin chiar condiția cusăturii,
 *  ACELAȘI cu capătul bucății dinainte. */
const faraPunctulDePornire = (p: string): string =>
  p.replace(/^M\s*-?[\d.]+\s*,?\s*-?[\d.]+\s*/, "");

export const cusut = (d: string): string => {
  const parti = d.split(/(?=M)/).map((x) => x.trim()).filter(Boolean);
  if (parti.length < 2) return d;
  let out = parti[0];
  for (let i = 1; i < parti.length; i++) {
    const c = capatulDrumului(out);
    const s0 = inceputulDrumului(parti[i]);
    out += c && s0 && c === s0
      ? ` ${faraPunctulDePornire(parti[i]).trim()}` : ` ${parti[i]}`;
  }
  return out;
};

/** SIMBOLURILE CARE PRIMESC HÂRTIE SUB TOT DREPTUNGHIUL LOR.
 *
 *  DE CE NU TOATE. Hârtia de sub un simbol se face din REUNIUNEA drumurilor
 *  lui, iar asta e de ajuns pentru cele desenate ca forme închise: o canapea
 *  își acoperă 62 % din cutie, un pat 96 % — destul cât să nu se mai vadă
 *  pardoseala prin ele (chiar cazul care a cerut umplerea: «se vedea
 *  pardoseala prin canapea»). Dar unele simboluri sunt scoase din planșă ca
 *  LINII DESCHISE, nu ca forme, și atunci reuniunea e goală. Măsurat pe toate
 *  cele 20 de simboluri: cuptorul, frigiderul, mașina de vase și vasul de WC
 *  n-au NICIO buclă închisă (0 %), lavoarul, chiuveta și cabina de duș au sub
 *  1 %, comoda TV 4 %. Prin ele se vedea hașura pardoselii — «anumite obiecte
 *  încă nu ascund hașura (ex. lavoarul)» (21.10.2026).
 *
 *  UN DREPTUNGHI, NU UN CONTUR ÎNCHIS. Închizând conturul desenat s-ar fi
 *  adăugat tuș pe planșă — iar piesele astea au deja desenul lor, nu le
 *  trebuie o ramă. Hârtia stă DEDESUBT și nu se vede: acoperă hașura, nimic
 *  mai mult.
 *
 *  LISTA E EXPLICITĂ, nu un prag pe măsurătoare, din același motiv ca
 *  `SIMBOL_PE_FEL`: ea spune «astea sunt corpuri pline», ceea ce e o însușire a
 *  OBIECTULUI, nu a desenului. Ușa are și ea sub 10 %, dar e un arc de
 *  deschidere, nu un corp — o foaie de hârtie sub ea ar fi acoperit pardoseala
 *  camerei degeaba. */
const CU_HARTIE = new Set([
  "cuptor", "frigider", "masina_spalat_vase", "wc",
  "lavoar_baie", "chiuveta_bucatarie", "dus", "comoda_tv",
]);

/** UN SIMBOL SCOS DIN PLANȘA DE REFERINȚĂ, așezat în cutia piesei.
 *
 *  Vectorii vin din `simboluriCad` — citiți din `layout.pdf`, nu desenați după
 *  ochi. Fiecare simbol își poartă MĂRIMEA ADEVĂRATĂ (`w`/`h`, în metri) și
 *  desenul în puncte (`pt`); aici se face doar potrivirea în dreptunghiul pe
 *  care îl scrie documentul.
 *
 *  SE ÎNTINDE PE AMÂNDOUĂ AXELE, nu se încadrează păstrând raportul: cutia e
 *  ce a scris omul sau mobilarea automată, iar un pat de 1,60 × 2,00 trebuie
 *  să umple 1,60 × 2,00 — un simbol centrat cu margini albe s-ar citi ca o
 *  piesă mai mică decât e. Raportul simbolului rămâne oricum în `w`/`h`, de
 *  unde se poate vedea cât de departe a fost întins.
 *
 *  TUȘUL E AL BIBLIOTECII (`LINIE`), nu al PDF-ului: un simbol adus trebuie să
 *  se așeze lângă unul desenat de mână fără să se vadă de unde vine. */
const Simbol = ({ nume, rot, margine, corp, w, h }: {
  nume: string; rot?: number; margine?: MargineaSimbolului;
  corp?: [number, number, number, number]; w: number; h: number;
}) => {
  const s = SIMBOLURI[nume];
  if (!s || !(s.pt[0] > 0) || !(s.pt[1] > 0)) return null;
  // THE MARGIN IS THE EXCEPTION to the stretch below: a sink's box is the
  // cabinet cut-out and its drawing is the bowl, so the architect asked for
  // the drawing to stop short of the box (sheet row 47, 25.10.2026). Written
  // on the piece's frame (sides along `w`, front and back along `h`), so the
  // rotation of the piece as a whole keeps it on the right edges. Capped at a
  // quarter of each side: a box shrunk below the margins must still draw.
  const lat = Math.min(margine?.lat ?? 0, w / 4);
  const fata = Math.min(margine?.fata ?? 0, h / 4);
  const wd = w - 2 * lat, hd = h - 2 * fata;
  // ÎNCOTRO STĂ SIMBOLUL: ce scrie pe fel, iar dacă nu scrie nimic, ce spune
  // raportul cutiei.
  //
  // RAPORTUL NU ȘTIE UNDE E CĂPĂTÂIUL. Simbolul patului e desenat culcat —
  // 1,95 × 1,50, cu tăblia la dreapta —, iar felul nostru «pat» e 1,60 × 2,00:
  // întins ca atare, patul iese lățit și turtit, deci trebuie întors. Numai că
  // «întors» înseamnă două lucruri, iar raportul le potrivește la fel de bine
  // pe amândouă: la +90° tăblia cade la PICIOARELE patului, adică fix pe
  // peretele de care ar fi trebuit să stea. Raportul nimerește proporția și
  // greșește înțelesul — v. `SIMBOL_PE_FEL`, unde piesele cu față și spate își
  // scriu singure sfertul de tură.
  //
  // Ghicitul rămâne pentru simbolurile cărora așezarea nu le spune nimic — o
  // chiuvetă, un ghiveci —, unde n-are ce strica.
  const dep = (a: number, b: number) => Math.abs(Math.log((a / b) / (w / h)));
  const sfert = rot === undefined
    ? (dep(s.pt[1], s.pt[0]) < dep(s.pt[0], s.pt[1]) ? 90 : 0)
    : ((Math.round(rot / 90) * 90) % 360 + 360) % 360;
  const [pw, ph] = sfert === 90 || sfert === 270
    ? [s.pt[1], s.pt[0]] : [s.pt[0], s.pt[1]];
  // Fiecare sfert își duce desenul înapoi în colțul din stânga-sus al cutiei
  // lui, de unde `scale` îl întinde. `rotate` singur l-ar scoate din ea.
  const aseaza = sfert === 90 ? ` rotate(90) translate(0 ${-s.pt[1]})`
    : sfert === 180 ? ` rotate(180) translate(${-s.pt[0]} ${-s.pt[1]})`
    : sfert === 270 ? ` rotate(-90) translate(${-s.pt[0]} 0)` : "";
  // With a `corp`, that part is what fills the box: turned like the drawing,
  // scaled onto wd × hd, its corner on the box's corner.
  let incadrare = `translate(${-w / 2 + lat} ${-h / 2 + fata}) `
    + `scale(${wd / pw} ${hd / ph})`;
  if (corp) {
    const intors = ([x, y]: [number, number]): [number, number] =>
      sfert === 90 ? [s.pt[1] - y, x] : sfert === 180 ? [s.pt[0] - x, s.pt[1] - y]
        : sfert === 270 ? [y, s.pt[0] - x] : [x, y];
    const [a, b] = [intors([corp[0], corp[1]]), intors([corp[2], corp[3]])];
    const [cx, cy] = [Math.min(a[0], b[0]), Math.min(a[1], b[1])];
    const sx = wd / Math.abs(a[0] - b[0]), sy = hd / Math.abs(a[1] - b[1]);
    incadrare = `translate(${-w / 2 + lat - sx * cx} ${-h / 2 + fata - sy * cy}) `
      + `scale(${sx} ${sy})`;
  }
  // An open path wholly outside `corp` (the arc) is a line, not a body: filled,
  // it would paint a white sliver over the floor beyond the box.
  const peDinafara = (d: string): boolean => {
    if (!corp || /z\s*$/i.test(d.trim())) return false;
    const n = (d.match(/-?\d*\.?\d+/g) ?? []).map(Number);
    const xs = n.filter((_, i) => i % 2 === 0), ys = n.filter((_, i) => i % 2 === 1);
    const e = 0.01;
    // Beyond an edge AND reaching past it: a jamb line lying on the edge is
    // the frame's, not the swing's.
    return (Math.max(...ys) <= corp[1] + e && Math.min(...ys) < corp[1] - e)
      || (Math.min(...ys) >= corp[3] - e && Math.max(...ys) > corp[3] + e)
      || (Math.max(...xs) <= corp[0] + e && Math.min(...xs) < corp[0] - e)
      || (Math.min(...xs) >= corp[2] - e && Math.max(...xs) > corp[2] + e);
  };
  return (
    <>
      {/* HÂRTIE SUB TOT SIMBOLUL, pentru corpurile pline desenate din linii
          deschise — v. `CU_HARTIE`. Fără contur: ea nu se vede, doar acoperă.
          In piece metres, over the whole box: with a margin, the strip around
          the drawing is cabinet, not floor. */}
      {CU_HARTIE.has(nume) && (
        <rect x={-w / 2} y={-h / 2} width={w} height={h}
              fill={FOND} stroke="none" />
      )}
    <g transform={incadrare + aseaza}>
      {/* PIESA NU E TRANSPARENTĂ — hârtie sub tot simbolul, apoi liniile lui.
          Se vedea pardoseala prin canapea: pe un parchet cu dungi, piesa
          arăta ca o sită. Piesele desenate de mână umplu de mult cu `FOND`
          (v. `corpDreptunghiular`); simbolurile scoase din planșă nu.

          SE UMPLE REUNIUNEA TUTUROR DRUMURILOR, nu cutia din jur și nici
          primul drum. Cutia ar da colțuri unui taburet rotund; primul drum
          merge doar la simbolurile care încep cu conturul, iar catalogul nu e
          așa peste tot — încercat, taburetul, comoda și măsuța rămâneau
          străvezii, fiindcă la ele conturul vine mai târziu. Umplut cu
          `nonzero`, nu `evenodd`: un detaliu desenat peste contur ar fi găurit
          piesa exact acolo unde e mai plină. */}
      <g fill={FOND} stroke="none">
        {s.d.map((d, i) => <path key={i} d={cusut(d)} fillRule="nonzero"
                                 fill={peDinafara(d) ? "none" : undefined} />)}
      </g>
      {s.d.map((d, i) => (
        <path key={i} d={d} fill="none"
              stroke={LINIE} style={{ strokeWidth: g(0.022) }}
              strokeDasharray={s.dash[i] ? "3 3" : undefined}
              strokeLinejoin="round" strokeLinecap="round"
              vectorEffect="non-scaling-stroke" />
      ))}
    </g>
    </>
  );
};

/** How far the drawing stops short of the box, in metres: `lat` at each side
 *  (along the piece's length), `fata` at the front and the back (along its
 *  depth). Only for pieces whose box is a cut-out around the drawing. */
export type MargineaSimbolului = { lat: number; fata: number };

/** CE FEL DE PIESĂ ARE SIMBOL DIN PLANȘĂ, ȘI ÎNCOTRO STĂ.
 *
 *  Numai felurile de mai jos: restul rămân desenate de mână, fiindcă planșa
 *  n-are simbol pentru ele. Tabelul e explicit, nu ghicit din nume — «masa
 *  rotundă» a noastră e masa de PRÂNZ (v. nota din catalog), iar `masa_rotunda`
 *  din planșă e cercul cu cărți, adică măsuța de cafea.
 *
 *  `rot` E SFERTUL DE TURĂ CU CARE SE AȘAZĂ DESENUL ÎN CUTIE, și se scrie
 *  numai pe piesele cu față și spate. Socotit așa încât spatele piesei să cadă
 *  pe latura de SUS a cutiei — acolo unde mobilarea pune peretele: o piesă
 *  așezată cu spatele la latura «v-» primește rotirea 0 (v. `mobilare::rotirea`),
 *  iar cutia vine gata întoarsă odată cu piesa. De-aia o singură cifră ajunge
 *  pentru toate cele patru așezări, și de-aia nu se poate deduce din raport:
 *  patul întors invers umple cutia la fel de bine, doar că doarme cu capul la
 *  picioare. Un fel fără `rot` se așază după raport (v. `Simbol`). */
export type SimbolulFelului = {
  nume: string; rot?: number; margine?: MargineaSimbolului;
  /** The part of the drawing that IS the piece, in the symbol's own points
   *  [x0, y0, x1, y1] before any turn. It fills the box; the rest of the
   *  drawing (a cabin's door and its arc) lands outside it at the same scale.
   *  Without it the whole drawing fills the box. */
  corp?: [number, number, number, number];
};

export const SIMBOL_PE_FEL: Record<string, string | SimbolulFelului> = {
  // Patul e desenat culcat, cu tăblia la dreapta: un sfert de tură la stânga
  // o duce sus, pe peretele de care stă patul.
  pat: { nume: "pat", rot: -90 },
  pat_o_persoana: { nume: "pat", rot: -90 },
  noptiera: "noptiera",
  // Canapelele sunt desenate în picioare, cu spătarul la stânga.
  canapea: { nume: "canapea_dreapta", rot: 90 },
  canapea_colt: { nume: "canapea", rot: 90 },
  // MASA DE PRÂNZ, FĂRĂ SCAUNE. Simbolul întreg al planșei (`masa_dining`) vine
  // cu două scaune desenate în el, la o mărime și într-un loc care nu sunt ale
  // camerei: întins în cutia mesei, le aduce înăuntrul ei, peste scaunele pe
  // care le pune mobilarea. Cerut scoase, textual: «the chairs from the
  // previous design are still there and they shouldn't be». Deci masa primește
  // blatul singur, tăiat din aceeași planșă, iar scaunele rămân treaba
  // mobilării, care știe câte încap și unde.
  //
  // `masa` și `birou` s-au întors la desenul de mână: sunt dreptunghiuri (1,60
  // × 0,90 și 1,40 × 0,70), iar planșa n-are masă dreptunghiulară — trimise tot
  // la `masa_dining`, o masă dreptunghiulară se citea rotundă, cu două scaune
  // strivite în ea.
  masa_rotunda: "masa_dining_fara_scaune",
  masuta_cafea: "masuta_cafea",
  masuta: "taburet_rotund",
  // Cercul din planșă, același pe care îl poartă și măsuța rotundă.
  taburet: "taburet_rotund",
  comoda_tv: "comoda_tv",
  // The sink stops short of its box: 3 cm at the sides, 5 cm towards the room
  // and the wall, the architect's own numbers (sheet row 47, 25.10.2026). The
  // box stays the cabinet cut-out that the kitchen front is laid from.
  chiuveta: { nume: "chiuveta_bucatarie", margine: { lat: 0.03, fata: 0.05 } },
  // ARAGAZUL, FRIGIDERUL ȘI VASUL și-au primit desenul lor (12.10.2026, cerut
  // de Bianca cu poze): ele NU mai trec pe simbolul din planșă, ci pe cazurile
  // de mai jos. Simbolurile vechi n-au dispărut — stau pe felurile `*_clasic`
  // din «Bibliotecă», ca să se poată pune la fel ca înainte.
  aragaz_clasic: "plita",
  cuptor: "cuptor",
  frigider_clasic: "frigider",
  wc_clasic: { nume: "wc", rot: 0 },
  masina_vase_clasic: "masina_spalat_vase",
  // LAVOARUL A IEȘIT DIN TABEL, și cu el din simbolul de planșă.
  //
  // `Simbol` întinde desenul pe AMÂNDOUĂ axele, dinadins (v. nota lui): un pat
  // de 1,60 × 2,00 trebuie să umple 1,60 × 2,00. Pe un lavoar asta e greșit —
  // blatul lățit trebuie să dea TEJGHEA, nu o cuvă mai mare. Arătat de arhitect
  // (22.10.2026): «când scalez pe orizontala lavoarul nu aș scala și zona din
  // interior; arată mai ok cel dublu», iar dublul e desenat de mână, cu cuvele
  // plafonate. Scos din tabel, felul cade pe desenul de mână de mai jos —
  // chiar plasa pe care o descrie `piesaSingura`.
  //
  // FĂRĂ UN `lavoar_clasic`: celelalte feluri scoase din tabel (aragazul,
  // frigiderul, vasul) și-au păstrat simbolul vechi pe un fel paralel fiindcă
  // acolo era o chestiune de GUST. Aici simbolul vechi nu e altfel, e stricat
  // la redimensionare — n-are rost păstrat ca să poată fi pus din nou.
  // Cabina de duș se deschide ÎN CAMERĂ, nu în perete: simbolul e desenat cu
  // ușa și arcul ei în partea de sus, deci o jumătate de tură o întoarce spre
  // odaie. Era așezat după raport — simbolul e aproape pătrat, deci nu-l
  // întorcea deloc —, și ușa bătea în peretele de care stă cabina.
  // The piece's w × h is the tray and its frame (sheet row 80): the extent of
  // the body's paths (7–19); the door and its arc stay outside, in the room.
  dus: { nume: "dus", rot: 180, corp: [-0.048, 21.6, 76.128, 77.347] },
  planta: "ghiveci",
};

const R = ({ x, y, w, h, rx = 0.03, sw = 0.025, fill = FOND }: {
  x: number; y: number; w: number; h: number;
  rx?: number; sw?: number; fill?: string;
}) => (
  <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill}
        stroke={LINIE} style={{ strokeWidth: g(sw) }} vectorEffect="non-scaling-stroke" />
);

const L = ({ x1, y1, x2, y2, sw = 0.018, punctat = false }: {
  x1: number; y1: number; x2: number; y2: number; sw?: number;
  /** Linia de deasupra planului de secționare — treptele rampei tăiate. */
  punctat?: boolean;
}) => (
  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={LINIE} style={{ strokeWidth: g(sw) }}
        strokeDasharray={punctat ? `${g(0.08)} ${g(0.06)}` : undefined}
        vectorEffect="non-scaling-stroke" />
);

const C = ({ cx, cy, r, sw = 0.018, fill = FOND }: {
  cx: number; cy: number; r: number; sw?: number; fill?: string;
}) => (
  <circle cx={cx} cy={cy} r={r} fill={fill} stroke={LINIE}
          style={{ strokeWidth: g(sw) }} vectorEffect="non-scaling-stroke" />
);

// ---------------------------------------------------------------------------
// AȘEZAREA: benzi de sus în jos, fiecare cu verificarea ei
// ---------------------------------------------------------------------------
//
// Piesele nu se suprapun fiindcă nu se pot suprapune: fiecare cameră se împarte
// în BENZI orizontale, un cursor coboară prin ele, iar o bandă se desenează doar
// dacă mai are loc. Ce nu încape se SARE — nu se micșorează și nu se înghesuie.
// Aceeași regulă ca în `arhMobilier` (v. `JOC` acolo), și e regula care lipsea:
// prima versiune așeza fotoliile și masa rotundă la coordonate fixe, iar pe o
// cameră mai îngustă ieșeau din ea și se retezau la decupaj — jumătăți de scaun
// lipite de zid.

/** Depărtarea de fața interioară a zidului. Cadrul camerei e chiar ea, deci o
 *  piesă lipită de `-h/2` atinge tencuiala. */
const M = 0.12;
/** Spațiul liber dintre două piese vecine. */
const JOC = 0.16;

/** CANAPEA: corp, spătar pe latura lungă, două brațe, două perne.
 *
 *  Spătarul și brațele sunt benzi DESENATE, nu sugerate printr-un contur mai
 *  gros: așa se recunoaște pe ce parte se stă, iar canapeaua lipită de zid
 *  arată încotro e camera. */
function Canapea({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const brat = Math.min(0.22, w * 0.12);
  const spatar = Math.min(0.2, h * 0.3);
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.05} />
      <L x1={x + brat} y1={y + spatar} x2={x + w - brat} y2={y + spatar} />
      <L x1={x + brat} y1={y + spatar} x2={x + brat} y2={y + h} />
      <L x1={x + w - brat} y1={y + spatar} x2={x + w - brat} y2={y + h} />
      <L x1={x + w / 2} y1={y + spatar} x2={x + w / 2} y2={y + h} />
    </g>
  );
}

/** FOTOLIU: același desen, la o singură pernă. */
/** FOTOLIUL UMPLE CUTIA PIESEI, nu un pătrat înscris în ea.
 *
 *  Era desenat pe `s = min(w, h)`: pe fotoliul de catalog, care era pătrat
 *  (0,80 × 0,80), ieșea la fel; de când are 0,65 × 0,75, un pătrat de 0,65 ar
 *  fi lăsat un centimetru de desen sub zona de apucat și 10 cm de cutie goală.
 *  Cerut anume: «De modificat "Box-ul" de selectie astfel incat sa fie de
 *  aceeasi dimensiune ca fotoliul» (19.10.2026) — iar cum zona de apucat e
 *  chiar `w × h` (v. `Desen::Mobila`), potrivirea se face desenând piesa pe
 *  toată cutia, nu strâmtând cutia.
 *
 *  BRAȚELE ȘI SPĂTARUL rămân proporționale cu latura mai scurtă, ca să nu se
 *  lățească odată cu piesa: un braț de fotoliu are cam 16 cm oricât ar fi
 *  șezutul de adânc. */
function Fotoliu({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const b = Math.min(w, h) * 0.16;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.05} />
      <L x1={x + b} y1={y + b} x2={x + w - b} y2={y + b} />
      <L x1={x + b} y1={y + b} x2={x + b} y2={y + h} />
      <L x1={x + w - b} y1={y + b} x2={x + w - b} y2={y + h} />
    </g>
  );
}

/** CANAPEAUA DE COLȚ: două brațe în L, cu spătarul pe laturile dinspre pereți.
 *  Nu e o canapea mai lată: e altă piesă, fiindcă ocupă un COLȚ — iar pe planșă
 *  tocmai colțul pe care-l ocupă e informația. */
function CanapeaColt({ w, h }: { w: number; h: number }) {
  const a = -w / 2, b = -h / 2;
  const gr = Math.min(0.92, Math.min(w, h) * 0.42);   // adâncimea șezutului
  const sp = Math.min(0.2, gr * 0.24);                // spătarul
  return (
    <g>
      <path d={`M${a} ${b}H${a + w}V${b + gr}H${a + gr}V${b + h}H${a}Z`}
            fill={FOND} stroke={LINIE} style={{ strokeWidth: g(0.025) }}
            vectorEffect="non-scaling-stroke" />
      <L x1={a + sp} y1={b + sp} x2={a + w - sp} y2={b + sp} />
      <L x1={a + sp} y1={b + sp} x2={a + sp} y2={b + h - sp} />
      <L x1={a + gr} y1={b + gr} x2={a + w - sp} y2={b + gr} />
      <L x1={a + gr} y1={b + gr} x2={a + gr} y2={b + h - sp} />
    </g>
  );
}

/** TELEVIZOR: bandă subțire pe perete, cu linia ecranului. */
function Tv({ x, y, w }: { x: number; y: number; w: number }) {
  return (
    <g>
      <R x={x} y={y} w={w} h={0.12} rx={0.02} sw={0.022} />
      <L x1={x + w * 0.2} y1={y + 0.12} x2={x + w * 0.8} y2={y + 0.12} sw={0.016} />
    </g>
  );
}

/** MASĂ ROTUNDĂ CU SCAUNE. `d` e raza până la marginea scaunelor, adică locul
 *  de care are nevoie piesa cu totul — cine o așază verifică pe ea, nu pe masă. */
function MasaRotunda({ cx, cy, r }: { cx: number; cy: number; r: number }) {
  const s = Math.min(0.42, r * 0.72);
  const d = r + s * 0.72;
  const scaun = (px: number, py: number, rot: number) => (
    <g transform={`translate(${px} ${py}) rotate(${rot})`}>
      <R x={-s / 2} y={-s / 2} w={s} h={s} rx={0.04} sw={0.02} />
      <L x1={-s / 2} y1={-s / 2 + s * 0.22} x2={s / 2} y2={-s / 2 + s * 0.22}
         sw={0.016} />
    </g>
  );
  return (
    <g>
      <C cx={cx} cy={cy} r={r} sw={0.025} />
      {scaun(cx, cy - d, 0)}
      {scaun(cx + d, cy, 90)}
      {scaun(cx, cy + d, 180)}
      {scaun(cx - d, cy, 270)}
    </g>
  );
}
/** Raza totală a mesei cu scaune cu tot. */
const razaMesei = (r: number) => r + Math.min(0.42, r * 0.72) * 0.72 + 0.21;

/** LIVING: canapea (cu fotolii lângă ea, dacă e loc), măsuță, masă rotundă,
 *  televizor pe peretele din față. Benzi de sus în jos — v. nota de sus. */
/** O PIESĂ AȘEZATĂ: felul ei din catalog și locul ei în camera-cadru.
 *
 *  `x`, `y` sunt CENTRUL piesei în coordonatele cadrului camerei (originea în
 *  mijlocul lui, `x` pe latura lungă), iar `rot` e rotirea ei față de cadru. */
export type Asezare = {
  kind: string; x: number; y: number; w: number; h: number; rot: number;
};

/** AȘEZAREA UNUI LIVING, ca date.
 *
 *  DE CE E SEPARATĂ DE DESEN. Mobilarea dedusă din felul camerei era doar
 *  desen: niște linii într-un `<g pointerEvents="none">`, pe care nu se putea
 *  apăsa. Se muta numai ce pusese cineva de mână — adică nimic, într-un modul
 *  din catalog, unde toată mobila e dedusă. „Nu putem muta mobilier?" de-aia.
 *
 *  Acum așezarea e o LISTĂ DE PIESE: desenul o randează, iar editorul o poate
 *  scrie în document când cineva apucă o canapea (v. `model::desfaMobilierul`).
 *  O singură socoteală pentru amândouă — două ar fi ajuns să pună canapeaua în
 *  două locuri diferite. */
export function asezareaLivingului(w: number, h: number): Asezare[] {
  const uw = w - 2 * M;                     // lățimea utilă
  const jos = h / 2 - M;                    // marginea de jos
  let y = -h / 2 + M;                       // cursorul
  const out: Asezare[] = [];

  // 1. Canapeaua, cu fotoliile de o parte și de alta când încap.
  const ch = 0.85;
  if (jos - y >= ch) {
    const fot = 0.68;
    const cuFotolii = uw >= 1.6 + 2 * (fot + JOC);
    const cw = Math.min(2.2, cuFotolii ? uw - 2 * (fot + JOC) : uw);
    out.push({ kind: "canapea", x: 0, y: y + ch / 2, w: cw, h: ch, rot: 0 });
    if (cuFotolii) {
      const fx = cw / 2 + JOC;
      out.push({ kind: "fotoliu", x: fx + fot / 2, y: y + ch / 2,
                 w: fot, h: fot, rot: -90 });
      out.push({ kind: "fotoliu", x: -fx - fot / 2, y: y + ch / 2,
                 w: fot, h: fot, rot: 90 });
    }
    y += ch + JOC;
  }

  // 2. Măsuța din fața canapelei.
  const mh = 0.55;
  if (jos - y >= mh) {
    const mw = Math.min(1.0, uw * 0.5);
    out.push({ kind: "masuta_cafea", x: 0, y: y + mh / 2, w: mw, h: mh, rot: 0 });
    y += mh + JOC;
  }

  // 3. Televizorul, LIPIT de peretele din față — se pune de jos în sus, deci
  //    banda lui se scade din ce rămâne, ca masa să nu intre peste el.
  const tvH = 0.12;
  const areTv = jos - y >= tvH;
  const josLiber = areTv ? jos - tvH - JOC : jos;

  // 4. Masa rotundă, în ce a mai rămas între măsuță și televizor.
  const r = 0.55;
  const rr = razaMesei(r);
  if (josLiber - y >= 2 * rr && uw >= 2 * rr) {
    out.push({ kind: "masa_rotunda", x: 0, y: josLiber - rr,
               w: 2 * r, h: 2 * r, rot: 0 });
  }
  if (areTv) {
    const tw = Math.min(1.1, uw * 0.4);
    out.push({ kind: "comoda_tv", x: 0, y: jos - tvH / 2, w: tw, h: tvH, rot: 0 });
  }
  return out;
}

/** AȘEZAREA UNUI DORMITOR, ca date — v. nota de la `asezareaLivingului`. */
export function asezareaDormitorului(w: number, h: number): Asezare[] {
  const uw = w - 2 * M, uh = h - 2 * M;
  const lat = Math.min(1.6, uw);
  const lung = Math.min(2.0, uh);
  if (lat < 0.9 || lung < 1.6) return [];        // nu încape un pat
  const x = -lat / 2, y = -h / 2 + M;
  const nop = 0.38;
  const cuNoptiere = uw >= lat + 2 * (nop + JOC);
  const out: Asezare[] = [
    { kind: "pat", x: 0, y: y + lung / 2, w: lat, h: lung, rot: 0 },
  ];
  if (cuNoptiere) {
    out.push({ kind: "noptiera", x: x - JOC - nop / 2, y: y + nop / 2,
               w: nop, h: nop, rot: 0 });
    out.push({ kind: "noptiera", x: x + lat + JOC + nop / 2, y: y + nop / 2,
               w: nop, h: nop, rot: 0 });
  }
  return out;
}

function Living({ w, h }: { w: number; h: number }) {
  // DESENUL SE FACE DIN AȘEZARE, nu pe lângă ea: dacă cele două s-ar socoti
  // separat, canapeaua de pe planșă și cea scrisă în document ar ajunge, la
  // prima schimbare, în locuri diferite.
  const asezare = asezareaLivingului(w, h);
  if (!asezare.length) return null;         // nu încape nimic: `null`, ca înainte
  return <g>{asezare.map((a, i) => <Asezata key={i} a={a} />)}</g>;
}

/** O piesă așezată, desenată cu semnul ei de planșă. */
function Asezata({ a }: { a: Asezare }) {
  const desen = (() => {
    switch (a.kind) {
      case "canapea": return <Canapea x={-a.w / 2} y={-a.h / 2} w={a.w} h={a.h} />;
      case "fotoliu": return <Fotoliu x={-a.w / 2} y={-a.h / 2} w={a.w} h={a.h} />;
      case "masuta_cafea":
        return <R x={-a.w / 2} y={-a.h / 2} w={a.w} h={a.h} rx={0.04} />;
      case "masa_rotunda":
        return <MasaRotunda cx={0} cy={0} r={a.w / 2} />;
      case "comoda_tv": return <Tv x={-a.w / 2} y={-a.h / 2} w={a.w} />;
      case "pat": return <PatDesen w={a.w} h={a.h} />;
      case "noptiera":
        return <R x={-a.w / 2} y={-a.h / 2} w={a.w} h={a.h} rx={0.03} sw={0.02} />;
      default: return null;
    }
  })();
  if (!desen) return null;
  // `rotate(0)` NU SE SCRIE. Nu e doar curățenie: o piesă nerotită într-un
  // `rotate(0)` arată, pentru cine citește markup-ul, exact ca una rotită — iar
  // proba care număra rotirile ca să vadă dacă au încăput fotoliile nu mai
  // putea să le deosebească.
  const t = a.rot
    ? `translate(${a.x} ${a.y}) rotate(${a.rot})`
    : `translate(${a.x} ${a.y})`;
  return <g transform={t}>{desen}</g>;
}

/** Corpul patului: pernele și cearșaful întors. Centrat în dreptunghiul lui. */
function PatDesen({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const perna = Math.min(0.42, w * 0.4);
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.04} />
      <R x={x + w * 0.06} y={y + 0.07} w={perna} h={0.28} rx={0.05} sw={0.018} />
      <R x={x + w - w * 0.06 - perna} y={y + 0.07} w={perna} h={0.28}
         rx={0.05} sw={0.018} />
      <L x1={x} y1={y + Math.min(0.52, h * 0.3)}
         x2={x + w} y2={y + Math.min(0.52, h * 0.3)} />
    </g>
  );
}

/** PAT: corp, două perne, cearșaf întors, noptiere când e loc. */
function Pat({ w, h }: { w: number; h: number }) {
  const asezare = asezareaDormitorului(w, h);
  if (!asezare.length) return null;         // nu încape nimic: `null`, ca înainte
  return <g>{asezare.map((a, i) => <Asezata key={i} a={a} />)}</g>;
}

/** BUCĂTĂRIE: blat pe peretele de sus, cu plita și chiuveta ÎN el, iar coloana
 *  pe latura scurtă. Aparatele se așază pe blat de la stânga la dreapta, fiecare
 *  doar dacă mai e blat sub el. */
function Bucatarie({ w, h }: { w: number; h: number }) {
  const b = 0.6;
  const uw = w - 2 * M;
  const st = -w / 2 + M, sus = -h / 2 + M;
  if (uw < 0.9 || h - 2 * M < b) return null;
  const lung = uw;
  let x = st + 0.18;                              // cursorul pe blat
  const pe: ReactNode[] = [];
  const plita = 0.5, chiuveta = 0.46;
  if (x + plita <= st + lung - 0.12) {
    pe.push(
      <R key="pl" x={x} y={sus + 0.09} w={plita} h={0.42} rx={0.03} sw={0.018} />,
      ...[0, 1].flatMap((i) => [0, 1].map((j) => (
        <C key={`o${i}${j}`} cx={x + 0.14 + i * 0.22} cy={sus + 0.21 + j * 0.18}
           r={0.062} sw={0.015} />
      ))));
    x += plita + JOC;
  }
  if (x + chiuveta <= st + lung - 0.12) {
    pe.push(
      <R key="ch" x={x} y={sus + 0.09} w={chiuveta} h={0.42} rx={0.04} sw={0.018} />,
      <C key="rb" cx={x + chiuveta / 2} cy={sus + 0.3} r={0.055} sw={0.015} />);
  }
  return (
    <g>
      <R x={st} y={sus} w={lung} h={b} rx={0.02} />
      {pe}
      {h - 2 * M >= b + JOC + 0.65 && (
        <R x={st} y={sus + b + JOC} w={b} h={0.65} rx={0.02} />
      )}
    </g>
  );
}

/** BAIE: cada pe peretele de jos, lavoarul și vasul pe cel de sus — fiecare
 *  doar dacă are loc pe peretele lui. Fără loc de cadă, în colțul de jos
 *  intră măcar o cabină de duș: o baie fără loc de spălat nu e o baie. */
function Baie({ w, h }: { w: number; h: number }) {
  const uw = w - 2 * M, uh = h - 2 * M;
  const st = -w / 2 + M, sus = -h / 2 + M, jos = h / 2 - M;
  const out: ReactNode[] = [];
  const cadaL = Math.min(1.7, uw), cadaH = 0.72;
  const areCada = cadaL >= 1.2 && uh >= cadaH + 0.5 + JOC;
  if (areCada) {
    out.push(
      <R key="cd" x={st} y={jos - cadaH} w={cadaL} h={cadaH} rx={0.09} />,
      <C key="sc" cx={st + cadaL - 0.22} cy={jos - cadaH / 2} r={0.055} sw={0.015} />);
  } else {
    const cab = Math.min(0.9, uw, uh - 0.5 - JOC);
    if (cab >= 0.7) out.push(<CabinaDus key="ds" x={st} y={jos - cab} s={cab} />);
  }
  // peretele de sus: lavoar, apoi vas
  let x = st;
  const lav = 0.58, vas = 0.38;
  if (x + lav <= st + uw) {
    out.push(
      <R key="lv" x={x} y={sus} w={lav} h={0.44} rx={0.07} sw={0.02} />,
      <C key="rb" cx={x + lav / 2} cy={sus + 0.23} r={0.06} sw={0.015} />);
    x += lav + JOC;
  }
  if (x + vas <= st + uw) out.push(<Vas key="vs" x={x} y={sus} />);
  return <g>{out}</g>;
}

/** VAS DE CLOSET: rezervorul dreptunghiular și cuva rotunjită. */
/** VASUL DE WC, desenul cerut de Bianca (12.10.2026, varianta A): rezervorul
 *  ca o bandă la perete, iar capacul UN SINGUR contur peste cuvă — laturi
 *  drepte, rotunjit la față. Fără al doilea contur pe dinăuntru: «fără linia
 *  dublă».
 *
 *  `rezervor` stins = WC suspendat: acolo rezervorul e în perete, deci nu se
 *  desenează nimic în spatele cuvei. */
function Vas({ x, y, w = 0.4, h = 0.62, rezervor = true }: {
  x: number; y: number; w?: number; h?: number; rezervor?: boolean;
}) {
  const rez = rezervor ? Math.min(0.13, h * 0.22) : 0;
  const r = w / 2;
  const cuva = `M${x} ${y + rez}H${x + w}V${y + h - r}`
    + `A${r} ${r} 0 0 1 ${x + w - r} ${y + h}`
    + `H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}Z`;
  return (
    <g>
      {rezervor && <R x={x} y={y} w={w} h={rez} rx={0.02} sw={0.021} />}
      <path d={cuva} fill={FOND} stroke={LINIE}
            style={{ strokeWidth: g(0.022) }} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/** PLITA ELECTRICĂ: patru ochiuri, două mari și două mici pe diagonale, cu
 *  linia de sus (varianta B, aleasă de Bianca). */
function Plita({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const mare = Math.min(w, h) * 0.19;
  const mic = Math.min(w, h) * 0.135;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.025} />
      <L x1={x + 0.02} y1={y + h * 0.14} x2={x + w - 0.02} y2={y + h * 0.14}
         sw={0.016} />
      {([[0.32, 0.37, mic], [0.70, 0.37, mare],
         [0.31, 0.73, mare], [0.70, 0.73, mic]] as const).map(([ux, uy, r], i) => (
        <C key={i} cx={x + w * ux} cy={y + h * uy} r={r} sw={0.017} />
      ))}
    </g>
  );
}

/** FRIGIDERUL, vedere de sus: corpul, ușa ca bandă pe față, balamaua groasă
 *  peste linia ușii și mânerul scurt la colțul opus (varianta B). */
function Frigider({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const usa = Math.min(0.055, h * 0.1);
  const linia = y + h - usa;
  return (
    <g>
      <R x={x} y={y} w={w} h={h - usa} rx={0.02} />
      <R x={x} y={linia} w={w} h={usa} rx={0.015} sw={0.019} />
      <L x1={x + 0.07} y1={linia - 0.028} x2={x + 0.07} y2={linia + usa + 0.006}
         sw={0.036} />
      <R x={x + w * 0.74} y={y + h} w={w * 0.21} h={0.02} rx={0.012} sw={0.016} />
    </g>
  );
}

/** CABINA DE DUȘ — SEMNUL STANDARD DE PLANȘĂ: tăvița pătrată cu cele patru
 *  diagonale care coboară la sifonul din mijloc.
 *
 *  Așa se desenează un duș pe orice planșă de arhitectură, în orice birou:
 *  pătratul e tăvița (cu bordura ei, conturul interior), diagonalele sunt
 *  pantele spre scurgere, cercul e sifonul. Semnul se citește fără legendă și
 *  nu se confundă cu nimic altceva de 90 pe 90 — un corp de bucătărie e gol,
 *  o mașină are hublou, iar un pătrat cu un cerc singur ar fi o măsuță cu
 *  ceva pe ea. Diagonalele SE OPRESC LA SIFON, nu se încrucișează prin el:
 *  cercul rămâne alb și se vede.
 *
 *  Desenată în cutia `(x, y, s)`, pe convenția restului bibliotecii: contur
 *  negru pe hârtie, grosimi constante în pixeli. */
function CabinaDus({ x, y, s }: { x: number; y: number; s: number }) {
  const b = Math.min(0.05, s * 0.06);
  const r = Math.min(0.06, s * 0.07);
  const x0 = x + b, y0 = y + b, x1 = x + s - b, y1 = y + s - b;
  const cx = x + s / 2, cy = y + s / 2;
  // Capătul diagonalei, la marginea sifonului: fracția din drumul centru →
  // colț la care se ajunge la raza `r` (jumătatea diagonalei e (s − 2b)/√2).
  const k = (r * Math.SQRT2) / (s - 2 * b);
  const la = (px: number, py: number): [number, number] =>
    [cx + (px - cx) * k, cy + (py - cy) * k];
  const colturi: [number, number][] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
  return (
    <g>
      <R x={x} y={y} w={s} h={s} rx={0.02} />
      <R x={x0} y={y0} w={s - 2 * b} h={s - 2 * b} rx={0.015} sw={0.014} />
      {colturi.map(([px, py], i) => {
        const [qx, qy] = la(px, py);
        return <L key={i} x1={px} y1={py} x2={qx} y2={qy} sw={0.014} />;
      })}
      <C cx={cx} cy={cy} r={r} sw={0.015} />
    </g>
  );
}

/** DUȘ ca FUNCȚIUNE DE CAMERĂ: cabina lipită de peretele de sus, în
 *  mijloc, cât încape în cameră (până la 0,90). */
function Dus({ w, h }: { w: number; h: number }) {
  const s = Math.min(0.9, Math.min(w, h) - 2 * M);
  if (s < 0.6) return null;
  return <CabinaDus x={-s / 2} y={-h / 2 + M} s={s} />;
}

/** WC de serviciu: vasul și, dacă e loc, un lavoar mic lângă el. */
function Wc({ w, h }: { w: number; h: number }) {
  const uw = w - 2 * M, st = -w / 2 + M, sus = -h / 2 + M;
  if (uw < 0.42 || h - 2 * M < 0.6) return null;
  return (
    <g>
      <Vas x={st} y={sus} />
      {uw >= 0.38 + JOC + 0.42 && (
        <R x={st + 0.38 + JOC} y={sus} w={0.42} h={0.34} rx={0.06} sw={0.02} />
      )}
    </g>
  );
}

/** DEBARA / CĂMARĂ: rafturi pe peretele din stânga. */
function Camara({ w, h }: { w: number; h: number }) {
  const uh = h - 2 * M;
  if (w - 2 * M < 0.5 || uh < 0.5) return null;
  const n = Math.max(1, Math.floor(uh / 0.5));
  const x = -w / 2 + M, y = -h / 2 + M;
  return (
    <g>
      <R x={x} y={y} w={0.42} h={uh} rx={0.02} />
      {Array.from({ length: n - 1 }, (_, i) => (
        <L key={i} x1={x} y1={y + ((i + 1) * uh) / n}
           x2={x + 0.42} y2={y + ((i + 1) * uh) / n} sw={0.016} />
      ))}
    </g>
  );
}

/** Ce piesă primește fiecare funcțiune. Camerele fără mobilier (holuri,
 *  balcoane) rămân goale ANUME: golul e informație, nu lipsă. */
export function piesaPlan(kind: string, w: number, h: number): ReactNode {
  switch (kind) {
    // Masa rotundă intră tot prin `Living`, în banda ei — nu pusă peste, cum
    // era: acolo cădea peste televizor pe camerele scurte.
    case "living": return <Living w={w} h={h} />;
    case "bedroom": case "dormitor": return <Pat w={w} h={h} />;
    case "kitchen": return <Bucatarie w={w} h={h} />;
    case "bath": case "bath2": return <Baie w={w} h={h} />;
    case "shower": return <Dus w={w} h={h} />;
    case "wc": return <Wc w={w} h={h} />;
    case "store": return <Camara w={w} h={h} />;
    default: return null;
  }
}

/** AȘEZAREA UNEI CAMERE, pe fel — partea de mobilier care se poate apuca.
 *
 *  Numai livingul și dormitorul: acolo sunt PIESE, iar o piesă are sens mutată
 *  singură. Bucătăria, baia, dușul și cămara desenează INSTALAȚII și corpuri
 *  fixe — un blat cu plita și chiuveta în el, o cadă, un vas —, iar acelea nu
 *  se mută una câte una: chiuveta scoasă din blat nu e mobilier, e o greșeală
 *  de desen. Rămân deci desen, și rămân acolo unde le pune camera. */
/** Cadrul interior al unei camere — re-exportat de-aici ca editorul să nu
 *  importe două module pentru un singur gest. E aceeași socoteală pe care o
 *  face și desenul. */
export { cadru as cadruCamerei } from "./arhMobilier";

export function asezareaCamerei(kind: string, w: number, h: number): Asezare[] {
  switch (kind) {
    case "living": return asezareaLivingului(w, h);
    case "bedroom": case "dormitor": return asezareaDormitorului(w, h);
    default: return [];
  }
}

/** Funcțiunile pe care biblioteca asta le desenează. Restul (hol, balcon,
 *  scară, lift, ghenă) rămân la `arhMobilier`: sunt semne de planșă, aceleași
 *  pe amândouă convențiile. */
export const MOBILATE_PLAN = new Set([
  "living", "bedroom", "dormitor", "kitchen",
  "bath", "bath2", "shower", "wc", "store",
]);

// ---------------------------------------------------- piese de sine stătătoare ---
//
// Până aici biblioteca desena MOBILAREA UNEI CAMERE: îi dai un dreptunghi și
// felul camerei, ea așază patul, noptierele și dulapul unde încap. Bun pentru un
// plan dedus, unde nimeni nu pune obiectele unul câte unul.
//
// Editorul are însă nevoie și de cealaltă unitate: O PIESĂ. Cine mobilează de
// mână pune o canapea, o rotește, o mută lângă fereastră. Piesele de mai jos
// sunt aceleași desene, scoase din compozițiile de cameră și centrate în
// dreptunghiul lor, ca să poată fi așezate una câte una.

/** CATALOGUL DE PIESE: ce se poate pune, cu măsurile obișnuite ale unei
 *  planșe (m), cu categoria din paletă și cu modelul CC0 care le îmbracă în
 *  machetă.
 *
 *  O SINGURĂ LISTĂ PENTRU AMÂNDOUĂ DESENELE, și e chiar lucrul care lipsea.
 *  Modelele `.glb` erau legate de feluri într-un tabel din `tronson/Randare3D`,
 *  iar măsurile aici — două locuri, deci două ocazii ca o canapea nouă să apară
 *  pe plan și să lipsească din machetă. Acum felul, măsura, simbolul și modelul
 *  stau pe același rând.
 *
 *  `model` e numele fișierului din `public/arh3d/mobilier/<model>.glb` (Kenney
 *  Furniture Kit, CC0 — v. `ui/lab/descarca_materiale.py`). Lipsă = piesa NU se
 *  îmbracă dintr-un model: scara și liftul își schimbă lățimea, lungimea și
 *  numărul de trepte odată cu ce trage omul de ele, iar un `.glb` întins pe alt
 *  gabarit ar da trepte în pantă — volumele lor se fac pe loc, din măsuri.
 *
 *  `z` e cota tălpii, pentru ce ATÂRNĂ: televizorul de pe perete, corpul
 *  suspendat de bucătărie, hota. Restul stau pe jos, deci n-o scriu. */
export type FelPiesa = {
  kind: string; nume: string; w: number; h: number;
  /** Grupa din paleta de mobilare. */
  categorie: string;
  /** Modelul CC0, fără extensie. */
  model?: string;
  /** Cota tălpii, când piesa nu stă pe pardoseală. */
  z?: number;
  /** DESENUL PIESEI, când nu are unul scris de mână.
   *
   *  Poliliniile obiectului, în metri, cu originea în colțul lui stânga-jos —
   *  exact ce scoate `importer/dwg/biblioteca.py` dintr-un bloc de CAD. Cele
   *  ~40 de piese de casă au fiecare simbolul ei desenat în cod, fiindcă sunt
   *  puține și se folosesc peste tot; o bibliotecă adusă din DWG are sute, și
   *  nimeni nu le desenează de mână. Deci: dacă `piesaSingura` n-are un caz
   *  pentru `kind`, se desenează conturul ăsta, întins în cutia piesei. */
  contur?: number[][][];
  /** ÎNĂLȚIMEA OBIȘNUITĂ A PIESEI, în metri — a treia măsură, lângă `w` și `h`.
   *
   *  Un obiect se descrie lungime × lățime × înălțime; primele două erau aici
   *  de la început, a treia lipsea cu totul, iar panoul n-avea ce arăta
   *  (01.10.2026: «why can't i see inaltime?»).
   *
   *  E IMPLICITUL, nu o măsură a piesei puse: ce se scrie în document bate
   *  cifra de-aici, ca la lățime și adâncime. Cele de mai jos sunt înălțimile
   *  de catalog ale mobilei obișnuite — blat 0,90, masă 0,75, șezut 0,85 cu
   *  spătar — și se schimbă din panou, piesă cu piesă.
   *
   *  DEOCAMDATĂ E O DECLARAȚIE, nu geometrie: macheta folosește modelele CC0,
   *  fiecare cu înălțimea lui (v. `Randare3D`), deci cifra se scrie și se
   *  citește, dar încă nu modelează nimic. */
  inaltime?: number;
  /** Nu e mobilier: se pune din «Structură», nu din lista de piese. Stă totuși
   *  aici fiindcă se DESENEAZĂ și se editează la fel — o a doua listă ar fi
   *  însemnat două locuri în care se ține o măsură. */
  structura?: boolean;
};

export const PIESE_MOBILIER: FelPiesa[] = [
  { kind: "scara", nume: "Scară dreaptă", w: 1.6, h: 3.8, inaltime: 2.8, structura: true,
    categorie: "Structură" },
  { kind: "scaraU", nume: "Scară în U", w: 2.6, h: 3.4, inaltime: 2.8, structura: true,
    categorie: "Structură" },
  // GHENA: puțul tehnic prin care urcă țevile. 40 × 60 e mărimea obișnuită pe
  // blocurile de locuințe — cerută anume de Olivia, 24.09. Stă în «Structură»,
  // lângă scară și lift, fiindcă e tot o gaură prin planșeu, nu mobilier; și
  // ca ele, se lipește de perete când o tragi (v. `lipesteCutia`).
  { kind: "ghena", nume: "Ghenă", w: 0.4, h: 0.6, inaltime: 2.8, structura: true,
    categorie: "Structură" },
  { kind: "lift", nume: "Lift", w: 1.6, h: 1.8, inaltime: 2.8, structura: true,
    categorie: "Structură" },

  { kind: "canapea", nume: "Canapea", w: 2.2, h: 0.8, inaltime: 0.85,
    categorie: "Șezut", model: "loungeSofa" },
  { kind: "canapea_colt", nume: "Canapea de colț", w: 2.6, h: 2.0, inaltime: 0.85,
    categorie: "Șezut", model: "loungeSofaCorner" },
  { kind: "fotoliu", nume: "Fotoliu", w: 0.65, h: 0.75, inaltime: 0.85,
    categorie: "Șezut", model: "loungeChair" },
  { kind: "scaun", nume: "Scaun", w: 0.4, h: 0.4, inaltime: 0.85,
    categorie: "Șezut", model: "chair" },
  // TABURETUL DE BAR nu e un scaun de masă. Stă la blat, n-are spătar, și pe
  // planșa de referință e desenat ca un cerc — patru într-un rând sub blatul
  // bucătăriei. Transcris ca «scaun», se citea ca încă patru scaune de
  // sufragerie în living: «we can't add dining chairs as bar stools».
  // 0,35, nu 0,40: un taburet de bar E mai mic decât un scaun de masă — n-are
  // spătar și n-are braț. Cifra a coborât odată cu scaunul (0,45 → 0,40,
  // 19.10.2026), ca deosebirea dintre ele să rămână una de mărime, nu doar de
  // nume; proba `simbolIncotro` o cere explicit.
  { kind: "taburet", nume: "Taburet", w: 0.35, h: 0.35, inaltime: 0.75,
    categorie: "Șezut", model: "chair" },

  { kind: "pat", nume: "Pat dublu", w: 1.6, h: 2.0, inaltime: 0.55,
    categorie: "Dormit", model: "bedDouble" },
  { kind: "pat_o_persoana", nume: "Pat o persoană", w: 0.9, h: 2.0, inaltime: 0.55,
    categorie: "Dormit", model: "bedSingle" },
  { kind: "noptiera", nume: "Noptieră", w: 0.45, h: 0.42, inaltime: 0.5,
    categorie: "Dormit", model: "cabinetBedDrawer" },
  { kind: "dulap", nume: "Dulap", w: 1.8, h: 0.6, inaltime: 2.2,
    categorie: "Dormit", model: "bookcaseClosedWide" },

  { kind: "masa", nume: "Masă", w: 1.2, h: 0.9, inaltime: 0.75,
    categorie: "Mese și depozitare", model: "table" },
  // MASA CU SCAUNE NU AJUNGE ÎN DOCUMENT: pusă din paletă, se desface pe loc
  // în blat și scaune legate prin `grup` (v. `model::adaugaMasaCuScaune`).
  // Rândul ăsta e aici ca să aibă buton, nume și mărime implicită ca orice
  // alt fel — nimic nu-l citește dintr-un document, fiindcă nimic nu-l scrie.
  { kind: "masa_scaune", nume: "Masă cu scaune", w: 1.2, h: 0.9, inaltime: 0.75,
    categorie: "Mese și depozitare", model: "table" },
  // MASA ROTUNDĂ E MASĂ DE PRÂNZ, nu măsuță: simbolul ei de pe planșă vine cu
  // patru scaune în jur. Modelul era `tableCoffee` — o măsuță de 40 cm
  // înălțime sub patru scaune de sufragerie, care în machetă se citea ca o
  // greșeală. Măsuța de cafea are de-acum rândul ei, mai jos.
  { kind: "masa_rotunda", nume: "Masă rotundă", w: 1.1, h: 1.1, inaltime: 0.75,
    categorie: "Mese și depozitare", model: "table" },
  { kind: "masuta_cafea", nume: "Măsuță de cafea", w: 1.05, h: 0.62, inaltime: 0.42,
    categorie: "Mese și depozitare", model: "tableCoffee" },
  { kind: "masuta", nume: "Măsuță", w: 0.5, h: 0.5, inaltime: 0.45,
    categorie: "Mese și depozitare", model: "sideTable" },
  { kind: "birou", nume: "Birou", w: 1.4, h: 0.7, inaltime: 0.75,
    categorie: "Mese și depozitare", model: "table" },
  { kind: "biblioteca", nume: "Bibliotecă", w: 1.1, h: 0.4, inaltime: 2.0,
    categorie: "Mese și depozitare", model: "bookcaseClosedWide" },
  { kind: "raft", nume: "Raft deschis", w: 0.9, h: 0.34, inaltime: 2.0,
    categorie: "Mese și depozitare", model: "bookcaseOpen" },
  { kind: "comoda_tv", nume: "Comodă TV", w: 1.4, h: 0.42, inaltime: 0.5,
    categorie: "Mese și depozitare", model: "cabinetTelevision" },
  { kind: "tv", nume: "Televizor", w: 1.2, h: 0.2, inaltime: 0.7, z: 1.1,
    categorie: "Mese și depozitare", model: "televisionModern" },

  { kind: "corp_bucatarie", nume: "Corp bucătărie", w: 0.6, h: 0.6, inaltime: 0.9,
    categorie: "Bucătărie", model: "kitchenCabinet" },
  // INSULA DE BUCĂTĂRIE — un dreptunghi și atât.
  //
  // Fără simbol de planșă și fără detalii desenate: o insulă e ce hotărăște
  // omul că e — blat de gătit, de spălat sau numai de stat în jurul lui —, iar
  // un aragaz sau o chiuvetă desenate în ea ar fi spus ceva ce nu se știe.
  // Mărimea de pornire e a unei insule obișnuite; se schimbă din panou, ca
  // orice piesă (v. `Proprietati`, latime/adâncime).
  { kind: "insula_bucatarie", nume: "Insulă de bucătărie", w: 1.8, h: 0.9, inaltime: 0.9,
    categorie: "Bucătărie", model: "kitchenCabinet" },
  // INSULA CU CHIUVETĂ: aceeași insulă, cu cuva tăiată în blat. E un fel
  // aparte, nu o insulă cu o chiuvetă pusă peste ea, din același motiv pentru
  // care lavoarul dublu e o piesă: cuva din blat nu se mută singură, iar
  // racordul de apă al insulei atârnă de ea. Cerut de Bianca, 12.10.2026.
  { kind: "insula_chiuveta", nume: "Insulă cu chiuvetă", w: 1.8, h: 0.9, inaltime: 0.9,
    categorie: "Bucătărie", model: "kitchenSink" },
  { kind: "corp_sertare", nume: "Corp cu sertare", w: 0.6, h: 0.6, inaltime: 0.9,
    categorie: "Bucătărie", model: "kitchenCabinetDrawer" },
  { kind: "corp_suspendat", nume: "Corp suspendat", w: 0.6, h: 0.35, inaltime: 0.7, z: 1.45,
    categorie: "Bucătărie", model: "kitchenCabinetUpper" },
  { kind: "chiuveta", nume: "Chiuvetă", w: 0.6, h: 0.5, inaltime: 0.9,
    categorie: "Bucătărie", model: "kitchenSink" },

  // ELECTROCASNICELE AU CATEGORIA LOR, nu stau în «Bucătărie».
  //
  // Se așază tot în frontul de corpuri, la aceleași 60 cm, deci mult timp au
  // stat acolo. Dar nu se ALEG odată cu el: un front de bucătărie se desenează
  // din corpuri identice, iar aragazul, frigiderul și mașinile sunt cinci
  // obiecte anume, fiecare cu locul lui — și, mai ales, fiecare cu un circuit
  // electric și o racordare în spate. Cine desenează bucătăria pune corpuri;
  // cine pune electrocasnicele face alt gest, la alt moment, și le caută la un
  // loc. Vezi grupa «Electrocasnice» din `editor/Paleta`.
  { kind: "aragaz", nume: "Aragaz", w: 0.6, h: 0.6, inaltime: 0.9,
    categorie: "Electrocasnice", model: "kitchenStove" },
  // CUPTORUL, pe lângă aragaz: cel încorporat e desenat ca un dreptunghi
  // punctat SUB blat, separat de plită. Lipsea din catalog, deci importul nu
  // avea ce fel să-i dea și ieșea încă un aragaz — sau nimic. Modelul e al
  // aragazului: în kit-ul CC0 nu există cuptor singur, iar în machetă un front
  // alb de 60 e un front alb de 60 (același argument ca la mașina de vase).
  { kind: "cuptor", nume: "Cuptor", w: 0.6, h: 0.6, inaltime: 0.85,
    categorie: "Electrocasnice", model: "kitchenStove" },
  { kind: "hota", nume: "Hotă", w: 0.6, h: 0.5, inaltime: 0.6, z: 1.5,
    categorie: "Electrocasnice", model: "hoodModern" },
  { kind: "frigider", nume: "Frigider", w: 0.7, h: 0.7, inaltime: 1.8,
    categorie: "Electrocasnice", model: "kitchenFridge" },
  // Mașina de spălat vase: un corp de 60 cu ușa rabatabilă. Model: același
  // ca al mașinii de spălat — în kit-ul CC0 nu există altul, iar în machetă
  // un front alb de 60 e un front alb de 60.
  { kind: "masina_vase", nume: "Mașină de spălat vase", w: 0.6, h: 0.6, inaltime: 0.85,
    categorie: "Electrocasnice", model: "washer" },
  // Mașina de spălat rufe stă în bucătărie, în frontul de corpuri, nu în
  // baie — așa se proiectează apartamentele noastre și așa o pune și
  // mobilarea automată (`tronson/design/mobilare.ts`).
  { kind: "masina_spalat", nume: "Mașină de spălat rufe", w: 0.6, h: 0.6, inaltime: 0.85,
    categorie: "Electrocasnice", model: "washer" },

  { kind: "cada", nume: "Cadă", w: 1.7, h: 0.75, inaltime: 0.6,
    categorie: "Sanitare", model: "bathtub" },
  // A DOUA CADĂ ȘI AL DOILEA DUȘ (12.10.2026, Bianca, cu poze de pe planșele
  // ei): «add another option for the shower icon as well as for the tub one
  // (round tub, walk in shower)». Cele de dinainte RĂMÂN — sunt două obiecte
  // în plus, nu două obiecte schimbate: o baie cu cadă ovală și una cu cadă
  // dreaptă sunt băi diferite, iar planșa le desenează diferit.
  { kind: "cada_ovala", nume: "Cadă ovală", w: 1.7, h: 0.8, inaltime: 0.6,
    categorie: "Sanitare", model: "bathtub" },
  { kind: "dus", nume: "Cabină de duș", w: 0.9, h: 0.9, inaltime: 2.0,
    categorie: "Sanitare", model: "showerRound" },
  // For a tight bath where the cabin's hinged door would hit the WC (sheet
  // row 81): sliding glass on the two room sides, entered at their corner.
  // After `dus`, so `FEL_DIN_MODEL` still reads `showerRound` as the cabin.
  { kind: "dus_colt", nume: "Cabină de duș cu uși glisante", w: 0.9, h: 0.9,
    inaltime: 2.0, categorie: "Sanitare", model: "showerRound" },
  // DUȘUL FĂRĂ CABINĂ: tăvița la nivelul pardoselii, cu un singur panou de
  // sticlă pe o latură. Pe planșă se deosebește de cabină prin ce NU are —
  // nicio ușă, niciun arc — și prin formă: e dreptunghiular, nu pătrat.
  { kind: "dus_deschis", nume: "Duș fără cabină", w: 1.2, h: 0.9, inaltime: 2.0,
    categorie: "Sanitare", model: "showerRound" },
  { kind: "wc", nume: "WC", w: 0.4, h: 0.65, inaltime: 0.8,
    categorie: "Sanitare", model: "toilet" },
  { kind: "wc_suspendat", nume: "WC suspendat", w: 0.4, h: 0.6, inaltime: 0.55,
    categorie: "Sanitare", model: "toiletSquare" },
  { kind: "lavoar", nume: "Lavoar", w: 0.6, h: 0.45, inaltime: 0.85,
    categorie: "Sanitare", model: "bathroomSink" },
  // LAVOARUL DUBLU: DOUĂ CUVE PE UN SINGUR CORP, nu două lavoare alăturate.
  // Cerut de Bianca (12.10.2026) de pe planșele ei, unde băile mari îl au
  // peste tot. Contează că e o piesă, nu două: are un blat continuu, se mută
  // dintr-o bucată și se racordează o dată.
  { kind: "lavoar_dublu", nume: "Lavoar dublu", w: 1.2, h: 0.5, inaltime: 0.85,
    categorie: "Sanitare", model: "bathroomSink" },

  // MOBILAREA DE EXTERIOR — grupa cerută de Bianca (12.10.2026): pe balcoane
  // și terase nu stă mobilă de interior, iar importul n-avea ce să pună acolo.
  // Desenate după planșele ei: șezlongul cu perna capului peste corp, fotoliul
  // cu șezutul înăuntru, măsuța rotundă.
  { kind: "sezlong", nume: "Șezlong", w: 0.72, h: 2.0, inaltime: 0.35,
    categorie: "Exterior", model: "loungeChair" },
  { kind: "fotoliu_exterior", nume: "Fotoliu de exterior", w: 0.8, h: 0.8,
    inaltime: 0.75, categorie: "Exterior", model: "loungeChair" },
  { kind: "canapea_exterior", nume: "Canapea de exterior", w: 1.9, h: 0.85,
    inaltime: 0.75, categorie: "Exterior", model: "loungeSofa" },
  { kind: "masa_exterior", nume: "Masă de exterior", w: 1.4, h: 0.8,
    inaltime: 0.75, categorie: "Exterior", model: "diningTable" },
  { kind: "masuta_exterior", nume: "Măsuță rotundă de exterior", w: 0.55, h: 0.55,
    inaltime: 0.45, categorie: "Exterior", model: "coffeeTable" },
  { kind: "umbrela", nume: "Umbrelă", w: 1.6, h: 1.6, inaltime: 2.4,
    categorie: "Exterior" },
  { kind: "planta_terasa", nume: "Plantă de terasă", w: 0.6, h: 0.6,
    inaltime: 0.9, categorie: "Exterior", model: "plant" },

  // SIMBOLURILE DE DINAINTE, păstrate ca piese de sine stătătoare: felurile
  // obișnuite au primit desenul nou (12.10.2026), iar cine vrea simbolul vechi
  // îl pune de-aici. Stau în «Bibliotecă» fiindcă nu se aleg la import — ele
  // sunt pentru mâna omului.
  { kind: "wc_clasic", nume: "WC (simbol de planșă)", w: 0.4, h: 0.62,
    inaltime: 0.8, categorie: "Bibliotecă", model: "toilet" },
  { kind: "aragaz_clasic", nume: "Aragaz (simbol de planșă)", w: 0.6, h: 0.6,
    inaltime: 0.9, categorie: "Bibliotecă", model: "cooker" },
  { kind: "frigider_clasic", nume: "Frigider (simbol de planșă)", w: 0.7, h: 0.7,
    inaltime: 1.8, categorie: "Bibliotecă", model: "fridge" },
  { kind: "masina_vase_clasic", nume: "Mașină de spălat vase (simbol de planșă)",
    w: 0.6, h: 0.6, inaltime: 0.85, categorie: "Bibliotecă",
    model: "dishwasher" },

  { kind: "covor", nume: "Covor", w: 2.4, h: 1.7, inaltime: 0.01,
    categorie: "Iluminat și decor", model: "rugRectangle" },
  { kind: "covor_rotund", nume: "Covor rotund", w: 1.6, h: 1.6, inaltime: 0.01,
    categorie: "Iluminat și decor", model: "rugRound" },
  { kind: "veioza", nume: "Veioză", w: 0.26, h: 0.26, inaltime: 0.45, z: 0.55,
    categorie: "Iluminat și decor", model: "lampRoundTable" },
  { kind: "planta", nume: "Plantă", w: 0.5, h: 0.5, inaltime: 1.2,
    categorie: "Iluminat și decor", model: "pottedPlant" },
  { kind: "planta_mica", nume: "Plantă mică", w: 0.3, h: 0.3, inaltime: 0.45,
    categorie: "Iluminat și decor", model: "plantSmall1" },
];

/** Piesele casei ȘI cele aduse din CAD. Biblioteca vine la urmă, ca o piesă
 *  importată să nu poată acoperi din greșeală una scrisă de mână. */
export const TOATE_PIESELE: FelPiesa[] = [...PIESE_MOBILIER, ...BIBLIOTECA];

const PE_FEL = new Map(TOATE_PIESELE.map((p) => [p.kind, p]));
/** Felul unei piese, cu măsurile și modelul lui. */
export const felulPiesei = (kind: string): FelPiesa | undefined => PE_FEL.get(kind);

/** DRUMUL ÎNAPOI: de la modelul CC0 la felul nostru.
 *
 *  Îi trebuie baghetei. Așezarea automată (`plan/arhMobilier3d::catalog`) e
 *  scrisă în MODELE — ea a fost dintotdeauna un lucru al machetei —, iar ce se
 *  scrie în document sunt FELURI. Tabelul se face din catalog, nu de mână, ca
 *  să nu existe un al doilea loc unde se hotărăște ce model poartă un pat.
 *
 *  Un model folosit de mai multe feluri (`table` e și masă, și birou) rămâne la
 *  PRIMUL din catalog: e cel mai obișnuit, iar cine voia birou îl schimbă dintr-un
 *  click. Ordinea listei e deci parte din înțeles, nu întâmplare. */
export const FEL_DIN_MODEL: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const p of PIESE_MOBILIER) {
    if (p.model && !(p.model in out)) out[p.model] = p.kind;
  }
  return out;
})();

/** Categoriile, în ordinea în care le arată paleta. */
export const CATEGORII_MOBILIER = [
  "Șezut", "Dormit", "Mese și depozitare", "Bucătărie", "Electrocasnice",
  "Sanitare", "Iluminat și decor",
  // ADUSE DIN CAD, ultima grupă: sunt multe și nesortate, deci stau la urmă,
  // după piesele casei. V. `generated/biblioteca.ts`.
  "Bibliotecă",
];

/** NUMELE UNEI PIESE, în limba ecranului.
 *
 *  Catalogul ține `nume` în română fiindcă el e sursa tehnică — după el se
 *  caută în cod și în probe —, dar pe ecran numele trebuie să fie al limbii
 *  alese. Cheile sunt `mobilier.<kind>`, câte una de fiecare fel din catalog:
 *  un fel adăugat fără cheie se vede imediat, fiindcă i-ar apărea cheia crudă
 *  pe buton. Pentru un fel din afara catalogului — un document mai vechi —
 *  rămâne numele scris, care e tot mai bun decât o cheie. */
export const numePiesa = (kind: string): string => {
  const p = felulPiesei(kind);
  if (!p) return kind;
  // PIESELE ADUSE DIN CAD își poartă numele din desen: n-au cheie de traducere
  // și n-ar avea de unde s-o ia — sunt sute, cu nume date de cine a desenat
  // blocul. Se recunosc după `contur`, singurul lucru pe care piesele casei
  // nu-l au. V. `generated/biblioteca.ts`.
  if (p.contur) return p.nume;
  return t(`mobilier.${kind}` as Cheie);
};

/** Numele unei categorii din paleta de design, în limba ecranului. */
export const numeCategorie = (categorie: string): string =>
  t((CHEIE_CATEGORIE[categorie] ?? "categorie.decor") as Cheie);

const CHEIE_CATEGORIE: Record<string, string> = {
  // «Structură» — scara, liftul — nu era în listă, deci scara din paletă se
  // scria «Iluminat și decor» pe rândul ei de categorie.
  "Structură": "categorie.structura",
  "Electrocasnice": "categorie.electrocasnice",
  "Șezut": "categorie.sezut",
  "Dormit": "categorie.dormit",
  "Mese și depozitare": "categorie.mese",
  "Bucătărie": "categorie.bucatarie",
  "Sanitare": "categorie.sanitare",
  "Iluminat și decor": "categorie.decor",
  "Bibliotecă": "categorie.biblioteca",
};

/** OBIECTELE SANITARE — cada, dușul, vasul, lavoarul.
 *
 *  Sunt în același catalog cu mobilierul fiindcă se desenează și se așază la
 *  fel (un dreptunghi cu un desen în el, pus pe un perete), dar nu sunt
 *  mobilier: un lavoar cere o coloană de apă și una de scurgere, iar cine mută
 *  un vas de WC mută o instalație. De-aia paleta le ține într-o grupă proprie,
 *  «Instalații sanitare», lângă cea de electrice — v. `editor/Paleta`.
 *
 *  Chiuveta de bucătărie NU e aici: ea face parte din frontul de corpuri, se
 *  așază odată cu el și se alege dintre corpurile de bucătărie. Că are și ea
 *  apă și scurgere e adevărat, dar cine desenează o bucătărie caută chiuveta
 *  lângă blat, nu lângă cadă. */
/** COVOARELE — piesele peste care stă toată cealaltă mobilă.
 *
 *  Pe planșă covorul e desenat SUB pat și sub măsuță; în SVG «deasupra»
 *  înseamnă «desenat mai târziu», deci ele se desenează primele (v.
 *  `tronson/Desen::Mobila`). Lista stă aici, lângă catalog, ca un covor nou
 *  să n-o uite. */
export const ESTE_COVOR = new Set(
  PIESE_MOBILIER.filter((p) => p.kind.startsWith("covor")).map((p) => p.kind));

/** MOBILA DE EXTERIOR — ce se poate pune pe un balcon, pe o logie sau pe o
 *  terasă, și NUMAI ea (Bianca, 12.10.2026: «on the balcony we can only use
 *  exterior furniture not interior one»). Lista iese din catalog, ca o piesă
 *  nouă de exterior să intre în regulă fără s-o mai scrie nimeni încă o dată. */
/** CE SE DESENEAZĂ SUB CE — rangul fiecărei piese în ordinea de desenare.
 *
 *  În SVG «deasupra» înseamnă «desenat mai târziu», iar piesele veneau în
 *  ordinea din document, adică în ordinea în care le-a citit cititorul. Două
 *  lucruri ies greșit din asta, și amândouă au fost reclamate:
 *
 *    0. COVORUL stă sub mobilă (12.10.2026) — un covor transcris după pat îl
 *       acoperea;
 *    1. BLATUL ȘI INSULA stau sub aparatele montate în ele: «mobila de
 *       bucătărie e ascunsă de insulă; aragazul, chiuveta trebuie să stea
 *       peste insulă sau să se vadă. Dacă șterg insula, se văd» (arhitectul,
 *       12.10.2026). Plita tăiată în blat se desenează DUPĂ el, altfel blatul
 *       o acoperă — exact ce se vedea.
 *    2. restul, în ordinea lor.
 *
 *  Rangul se citește din catalog, nu dintr-o listă scrisă de mână: o piesă
 *  nouă de blat intră în regulă fiindcă îi spune categoria. */
export const SUB_ALTELE = new Set(
  PIESE_MOBILIER.filter((p) => p.kind.startsWith("insula")
                            || p.kind === "corp_bucatarie"
                            || p.kind === "corp_sertare").map((p) => p.kind));

export const rangDesen = (kind: string): number =>
  ESTE_COVOR.has(kind) ? 0 : SUB_ALTELE.has(kind) ? 1 : 2;

export const DE_EXTERIOR = new Set(
  PIESE_MOBILIER.filter((p) => p.categorie === "Exterior").map((p) => p.kind));

export const SANITARE = new Set(
  PIESE_MOBILIER.filter((p) => p.categorie === "Sanitare").map((p) => p.kind));

/** SCAUNUL: șezut cu spătar, orientat cu spatele în sus. */
function Scaun({ w, h }: { w: number; h: number }) {
  return (
    <g>
      <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.04} />
      <R x={-w / 2} y={-h / 2} w={w} h={Math.min(0.1, h * 0.25)} rx={0.03} />
    </g>
  );
}

/** MASA dreptunghiulară, cu scaunele pe laturile lungi cât încap. */
/** MASA, ATÂT — blatul, fără scaune.
 *
 *  Își desena singură câte un rând de scaune pe fiecare latură lungă, în afara
 *  cutiei ei. Pe un document care are și scaunele scrise ca piese — M9B are
 *  zece mese și cincizeci de scaune, așezate fiecare la locul și la rotirea
 *  lui — cele desenate cădeau peste cele adevărate: «the chairs of the dining
 *  table are overlapping». Nu erau prea multe scaune, erau două desene ale
 *  acelorași scaune.
 *
 *  UN SCAUN E O PIESĂ, nu un ornament al mesei. Numai așa poate fi mutat,
 *  numărat sau scos, și numai așa știe mobilarea automată că locul din jurul
 *  mesei e ocupat (v. `mobilare::living`, care de-acum le și pune). */
function Masa({ w, h }: { w: number; h: number }) {
  return <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.04} />;
}

/** MASA CU SCAUNELE EI, DESENATĂ ÎNTR-O BUCATĂ — numai pentru GLIFUL DIN
 *  PALETĂ (`Paleta` cheamă `piesaSingura` ca să deseneze butonul).
 *
 *  Pe planșă nu se desenează niciodată așa: acolo ansamblul e blat + scaune,
 *  fiecare piesa lui, iar `model::adaugaMasaCuScaune` îl desface la punere.
 *  Despărțirea asta e chiar lecția de la `masa_dining`: un simbol care-și
 *  desenează singur scaunele cade peste scaunele adevărate și se văd două
 *  rânduri. Butonul n-are scaune adevărate sub el, deci acolo e în regulă.
 *
 *  Scaunele vin din aceeași regulă ca pe planșă (`scauneleMesei`) și se
 *  desenează ÎNAINTEA blatului, ca blatul să le acopere capătul tras sub el —
 *  exact ce se vede în desen. */
function MasaCuScaune({ w, h }: { w: number; h: number }) {
  return (
    <g>
      {scauneleMesei([0, 0], w, h).map((s, i) => (
        <g key={i} transform={`translate(${s.c[0]} ${s.c[1]}) rotate(${s.deg})`}>
          <Scaun w={s.w} h={s.h} />
        </g>
      ))}
      <Masa w={w} h={h} />
    </g>
  );
}

/** CÂT DE LAT POATE FI UN CORP CA SĂ-ȘI MAI DESENEZE UȘA: 0,90 m.
 *
 *  Un corp de bucătărie are o ușă de 0,60, sau două de 0,45 pe un corp de
 *  0,90; mai lat de-atât nu e un corp, e un blat peste mai multe. */
const USA_DE_CORP = 0.9;

/** Corpurile dreptunghiulare simple — dulap, birou, aragaz, frigider — se
 *  deosebesc prin ce au desenat pe ele, nu prin contur. */
function Corp({ w, h, fel }: { w: number; h: number; fel: string }) {
  const x = -w / 2, y = -h / 2;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.02} />
      {(fel === "dulap" || fel === "comoda_tv")
        && <L x1={x} y1={y + h * 0.75} x2={x + w} y2={y + h * 0.75} />}
      {/* Corpurile de bucătărie se deosebesc între ele prin ce au pe ușă:
          unul are un mâner pe verticală, celălalt trei sertare. La 60 cm pe
          planșă, asta e tot ce încape — și e de-ajuns ca să se numere. */}
      {fel === "corp_bucatarie" && w <= USA_DE_CORP + 1e-9
        && <L x1={x + w * 0.5} y1={y + h * 0.62} x2={x + w * 0.5} y2={y + h * 0.9} sw={0.022} />}
      {fel === "corp_sertare" && [0.33, 0.66].map((v2, i) => (
        <L key={i} x1={x} y1={y + h * v2} x2={x + w} y2={y + h * v2} sw={0.016} />
      ))}
      {fel === "birou" && <L x1={x + 0.06} y1={y + h - 0.12} x2={x + w - 0.06} y2={y + h - 0.12} />}
      {fel === "aragaz" && [[0.28, 0.28], [0.72, 0.28], [0.28, 0.72], [0.72, 0.72]]
        .map(([u, v], i) => <C key={i} cx={x + w * u} cy={y + h * v} r={Math.min(w, h) * 0.13} />)}
      {fel === "frigider" && <L x1={x} y1={y + h * 0.38} x2={x + w} y2={y + h * 0.38} />}
      {/* Mașina de vase: panoul de comandă pe front și ușa rabatabilă,
          punctată, deschisă pe 60 cm în fața ei. */}
      {fel === "masina_vase" && (
        <>
          <L x1={x + 0.06} y1={y + h - 0.08} x2={x + w - 0.06} y2={y + h - 0.08} sw={0.016} />
          {/* Deasupra panoului de comandă, pe mijlocul corpului. */}
          <Sigla text="MSV" lat={w * 0.72} y={y + (h - 0.08) / 2} />
          <path d={`M${x} ${y + h}L${x} ${y + h + w * 0.9}L${x + w} ${y + h + w * 0.9}L${x + w} ${y + h}`}
                fill="none" stroke={LINIE} style={{ strokeWidth: g(0.014) }}
                strokeDasharray="0.06 0.05" vectorEffect="non-scaling-stroke" />
        </>
      )}
      {/* CUM SE DESCHID UȘILE, punctat — convenția planșei pentru ce se mișcă
          și pentru ce e deasupra tăieturii. Baleiajul spune cât loc cere piesa
          în fața ei: un frigider cu ușa la 70 cm de zidul din față nu se
          deschide, și asta se vede doar dacă ușa e desenată. Frigiderul și
          corpul de bucătărie au o foaie cu balamaua în colțul din stânga;
          dulapul are două, câte una pe fiecare jumătate. */}
      {fel === "frigider" && <Baleiaj x={x} y={y + h} w={w} />}
      {/* UȘA UNUI CORP DE BUCĂTĂRIE ARE CÂT CORPUL, nu cât blatul.
          Frontul strâns într-o singură piesă (v. `finalizare::
          uneste_fronturile_de_bucatarie`) poartă felul `corp_bucatarie` și
          ajunge la 2–3 m: baleiajul desenat pe toată lățimea lui ieșea un
          sfert de cerc cât jumătate de bucătărie — «ușa gigantică» (Bianca,
          12.10.2026). O ușă de corp nu trece de 0,60; peste atât, în fața
          piesei sunt mai multe uși, iar desenul lor una lângă alta ar face
          din planșă o dantelă. Deci: sub o ușă de dulap se desenează
          baleiajul, iar blatul lung rămâne blat. */}
      {fel === "corp_bucatarie" && w <= USA_DE_CORP + 1e-9
        && <Baleiaj x={x} y={y + h} w={w} />}
      {/* DULAPUL NU-ȘI DESENEAZĂ FOILE. Le-a avut, două, câte una pe fiecare
          jumătate — convenția de mai sus, pentru cât loc cere piesa în fața ei.
          Pe un dulap însă baleiajul încurcă mai mult decât spune: două
          sferturi de cerc care ies în cameră se citesc ca o piesă mai mare
          decât e, sau ca două uși care chiar stau deschise. Cerut scos:
          «for the wardrobe we shouldn't have the door openings». Frigiderul și
          corpul de bucătărie le păstrează — acolo întrebarea «se deschide?» e
          reală, fiindcă o ușă de frigider blocată face piesa nefolosibilă. */}
      {fel === "chiuveta" && (
        <>
          <R x={x + 0.06} y={y + 0.08} w={w - 0.12} h={h - 0.2} rx={0.04} />
          <C cx={x + w / 2} cy={y + h - 0.08} r={0.035} />
        </>
      )}
      {fel === "cada" && <R x={x + 0.08} y={y + 0.08} w={w - 0.16} h={h - 0.16} rx={0.12} />}
    </g>
  );
}

/** BALEIAJUL UNEI UȘI DE MOBILIER: foaia și arcul ei, PUNCTATE.
 *
 *  Balamaua e în `(x, y)`, pe frontul piesei (latura `+y`); foaia are `w` și
 *  se deschide la 90° spre `+y`, adică în cameră. `oglindit` pune balamaua la
 *  dreapta, pentru a doua ușă a unui dulap. Linia e întreruptă fiindcă nu e
 *  nimic tăiat: e o mișcare, desenată ca să se vadă cât loc cere. */
function Baleiaj({ x, y, w, oglindit = false }: {
  x: number; y: number; w: number; oglindit?: boolean;
}) {
  const s = oglindit ? -1 : 1;
  const d = `M${x} ${y}L${x} ${y + w}A${w} ${w} 0 0 ${oglindit ? 1 : 0} ${x + s * w} ${y}`;
  return (
    <path d={d} fill="none" stroke={LINIE} style={{ strokeWidth: g(0.014) }}
          strokeDasharray="0.06 0.05" vectorEffect="non-scaling-stroke" />
  );
}

/** ȘEZLONGUL: corpul lung, cu perna capului desenată peste el.
 *
 *  Varianta C, aleasă de Bianca: perna iese puțin din conturul corpului, cum
 *  se vede pe planșele ei, iar cuta de la genunchi e singura linie din corp. */
function Sezlong({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const m = Math.min(0.06, w * 0.09);
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.05} />
      <R x={x + m} y={y - 0.04} w={w - 2 * m} h={Math.min(0.5, h * 0.26)}
         rx={0.06} sw={0.02} />
      <L x1={x + m} y1={y + h * 0.62} x2={x + w - m} y2={y + h * 0.62} sw={0.015} />
    </g>
  );
}

/** FOTOLIUL ȘI CANAPEAUA DE EXTERIOR: conturul rotunjit, cu șezutul desenat
 *  înăuntru — brațele groase sunt chiar fâșia dintre cele două contururi. */
function FotoliuExterior({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.12} />
      <R x={x + 0.13} y={y + 0.17} w={w - 0.26} h={h - 0.25} rx={0.08}
         sw={0.018} />
    </g>
  );
}

function CanapeaExterior({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.1} />
      <R x={x + 0.07} y={y + 0.07} w={w - 0.14} h={Math.min(0.26, h * 0.3)}
         rx={0.05} sw={0.018} />
      {[1, 2].map((i) => (
        <L key={i} x1={x + (w * i) / 3} y1={y + h * 0.38}
           x2={x + (w * i) / 3} y2={y + h - 0.05} sw={0.015} />
      ))}
    </g>
  );
}

/** MASA DE EXTERIOR: tăblia cu muchia ei. Scaunele sunt piese, nu desen. */
function MasaExterior({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.05} />
      <R x={x + 0.06} y={y + 0.06} w={w - 0.12} h={h - 0.12} rx={0.03}
         sw={0.015} />
    </g>
  );
}

/** MĂSUȚA ROTUNDĂ DE TERASĂ: două cercuri, ca pe planșă. */
function MasutaExterior({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) / 2;
  return (
    <g>
      <C cx={0} cy={0} r={r} />
      <C cx={0} cy={0} r={Math.max(0.05, r - 0.09)} sw={0.015} />
    </g>
  );
}

/** UMBRELA: pânza, cu spițele și piciorul în mijloc. */
function Umbrela({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) / 2;
  return (
    <g>
      <C cx={0} cy={0} r={r} />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const a = (i * Math.PI) / 4;
        return (
          <L key={i} x1={0} y1={0} x2={Math.cos(a) * r} y2={Math.sin(a) * r}
             sw={0.014} />
        );
      })}
      <C cx={0} cy={0} r={0.05} sw={0.016} />
    </g>
  );
}

/** PLANTA DE TERASĂ: coroana din petale, ca să nu se citească drept măsuță
 *  rotundă la aceeași mărime. */
function PlantaTerasa({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) / 2;
  return (
    <g>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const a = (i * Math.PI) / 3;
        const cx = Math.cos(a) * r * 0.42;
        const cy = Math.sin(a) * r * 0.42;
        return (
          <ellipse key={i} cx={cx} cy={cy} rx={r * 0.42} ry={r * 0.3}
                   fill={FOND} stroke={LINIE}
                   transform={`rotate(${(a * 180) / Math.PI} ${cx} ${cy})`}
                   style={{ strokeWidth: g(0.015) }}
                   vectorEffect="non-scaling-stroke" />
        );
      })}
      <C cx={0} cy={0} r={r * 0.3} sw={0.016} />
    </g>
  );
}

/** COVORUL: un contur subțire cu franjuri pe laturile scurte.
 *
 *  SE DESENEAZĂ PRIMUL, iar de-acum CU HÂRTIE SUB EL. Fără umplere se vedea
 *  hașura pardoselii prin covor, ca printr-o sită — «anumite obiecte încă nu
 *  ascund hașura (ex. (...) covorul)» (21.10.2026). Umplerea nu acoperă mobila
 *  de deasupra tocmai fiindcă el e primul: mobila stă pe covor, nu sub el, și
 *  asta o dă ORDINEA, nu transparența. */
function Covor({ w, h, rotund }: { w: number; h: number; rotund?: boolean }) {
  const n = Math.max(3, Math.round(w / 0.12));
  if (rotund) {
    return (
      <g>
        <C cx={0} cy={0} r={Math.min(w, h) / 2} sw={0.02} />
        <C cx={0} cy={0} r={Math.min(w, h) / 2 - 0.1} sw={0.014} fill="none" />
      </g>
    );
  }
  return (
    <g>
      <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.02} sw={0.02} />
      <R x={-w / 2 + 0.09} y={-h / 2 + 0.09} w={w - 0.18} h={h - 0.18} rx={0.02}
         sw={0.014} fill="none" />
      {Array.from({ length: n }, (_, i) => {
        const x = -w / 2 + (w * (i + 0.5)) / n;
        return (
          <g key={i}>
            <L x1={x} y1={-h / 2} x2={x} y2={-h / 2 - 0.06} sw={0.012} />
            <L x1={x} y1={h / 2} x2={x} y2={h / 2 + 0.06} sw={0.012} />
          </g>
        );
      })}
    </g>
  );
}

/** NOPTIERA: un corp mic cu mânerul sertarului. Se deosebește de orice alt
 *  pătrat de 45 cm prin linia aia — pe o planșă, tot ce e la fel ca altceva se
 *  citește greșit. */
function Noptiera({ w, h }: { w: number; h: number }) {
  return (
    <g>
      <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.03} />
      <L x1={-w * 0.22} y1={0} x2={w * 0.22} y2={0} sw={0.022} />
    </g>
  );
}

/** CORPUL SUSPENDAT ȘI HOTA: linie întreruptă, fiindcă stau DEASUPRA tăieturii
 *  planului. Convenția de planșă pentru ce e peste cota de 1,15 m: se vede, dar
 *  nu se secționează. */
function Suspendat({ w, h, hota }: { w: number; h: number; hota?: boolean }) {
  return (
    <g>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} fill="none"
            stroke={LINIE} style={{ strokeWidth: g(0.02) }} strokeDasharray="0.1 0.07"
            vectorEffect="non-scaling-stroke" />
      {hota && (
        <>
          <L x1={-w / 2} y1={-h / 2} x2={w / 2} y2={h / 2} sw={0.014} />
          <L x1={w / 2} y1={-h / 2} x2={-w / 2} y2={h / 2} sw={0.014} />
        </>
      )}
    </g>
  );
}

/** RAFTUL DESCHIS: corpul și despărțitoarele. */
function Raft({ w, h }: { w: number; h: number }) {
  const n = Math.max(2, Math.round(w / 0.45));
  return (
    <g>
      <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.02} />
      {Array.from({ length: n - 1 }, (_, i) => {
        const x = -w / 2 + (w * (i + 1)) / n;
        return <L key={i} x1={x} y1={-h / 2} x2={x} y2={h / 2} sw={0.016} />;
      })}
    </g>
  );
}

/** LAVOARUL: cuva rotunjită în blat, cu bateria pe muchia dinspre perete. */
/** CÂT ARE CUVA UNUI LAVOAR, ȘI CÂT DE MICĂ POATE AJUNGE.
 *
 *  Cerut de arhitect (22.10.2026): «vreau B, unde chiuveta în sine nu crește,
 *  doar blatul», și, separat, «zona albastră n-ar trebui să poată fi mai mică
 *  de 25×15 cm». Mărimea de mai jos e cea a cuvei de pe un lavoar obișnuit de
 *  0,60 × 0,45 — de-acolo vine, nu dintr-o cifră aleasă frumos.
 *
 *  E ȘI PLAFONUL: un blat lățit dă tejghea, nu o cuvă mai mare. Deci cuva nu
 *  poate ajunge nici peste 100×50, maximul cerut în aceeași suflare — e
 *  satisfăcut din construcție, nu printr-o a doua strângere care n-ar prinde
 *  niciodată. */
const CUVA = { lat: 0.44, adanc: 0.31, latMin: 0.25, adancMin: 0.15 };
/** Cât blat rămâne în jurul cuvei înainte ca ea să dea înapoi: de-a latul, pe
 *  margini; pe adâncime, spatele cu bateria plus buza din față. */
const BLAT = { lat: 0.16, adanc: 0.14 };

/** LAVOARUL SIMPLU: un blat, o cuvă plafonată, bateria pe spate — aceeași
 *  alcătuire ca `LavoarDublu`, cu o singură cuvă. */
function Lavoar({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  // ROTUNJITE LA MILIMETRU: `0.6 - 0.16` dă 0,43999999999999995 în virgulă
  // mobilă, adică o cuvă cu un fir sub plafon tocmai pe lavoarul obișnuit — nu
  // se vede, dar înseamnă că mărimea ei ATÂRNĂ de a blatului, ceea ce e chiar
  // ce s-a cerut să nu se mai întâmple.
  const mm = (v: number) => Math.round(v * 1000) / 1000;
  const cw = mm(Math.min(Math.max(w - BLAT.lat, CUVA.latMin), CUVA.lat));
  const ch = mm(Math.min(Math.max(h - BLAT.adanc, CUVA.adancMin), CUVA.adanc));
  const cy = y + h * 0.54;
  const spate = y + 0.075;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.03} />
      <R x={-cw / 2} y={cy - ch / 2} w={cw} h={ch} rx={0.05} sw={0.018} />
      <C cx={0} cy={cy} r={0.024} sw={0.014} />
      {/* Gâtul bateriei nu poate ieși negativ pe un lavoar foarte puțin adânc,
          unde cuva urcă până peste spate. */}
      <Baterie cx={0} y={spate} lung={Math.max(0, cy - ch / 2 - spate)} />
    </g>
  );
}

/** LAVOARUL DUBLU: un singur blat, două cuve, două baterii.
 *
 *  Cuvele se desenează ca dreptunghiuri cu colțuri rotunde, nu ca cercuri — așa
 *  sunt pe planșa de la care a venit cererea, și așa se deosebesc dintr-o
 *  privire de două lavoare rotunde puse alături. Fiecare își are bateria pe
 *  spate, ca lavoarul simplu. */
/** BATERIA, ca în simbolul de lavoar al planșei: două robinete și gâtul care
 *  intră în cuvă. Desenată o dată, fiindcă o poartă și lavoarul dublu, și
 *  chiuveta insulei (Bianca, 12.10.2026: «add the focet»). */
function Baterie({ cx, y, lung }: { cx: number; y: number; lung: number }) {
  return (
    <g>
      <C cx={cx - 0.045} cy={y} r={0.026} sw={0.014} />
      <C cx={cx + 0.045} cy={y} r={0.026} sw={0.014} />
      <L x1={cx} y1={y} x2={cx} y2={y + lung} sw={0.02} />
    </g>
  );
}

function LavoarDublu({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const cw = Math.min(w / 2 - 0.1, 0.44);
  const ch = Math.max(0.12, h - 0.2);
  const cy = y + h * 0.54;
  const spate = y + 0.075;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.03} />
      {[-1, 1].map((s, i) => (
        <g key={i}>
          <R x={s * w * 0.26 - cw / 2} y={cy - ch / 2} w={cw} h={ch}
             rx={0.05} sw={0.018} />
          <C cx={s * w * 0.26} cy={cy} r={0.024} sw={0.014} />
          <Baterie cx={s * w * 0.26} y={spate} lung={cy - ch / 2 - spate} />
        </g>
      ))}
    </g>
  );
}

/** CADA OVALĂ: pastila dublă, cu scurgerea la un capăt.
 *
 *  Și conturul EXTERIOR e o pastilă, nu un dreptunghi: așa e desenată pe
 *  planșele din care vine cererea, și așa a fost aleasă (Bianca, 12.10.2026,
 *  varianta A). Cada zidită într-un postament drept rămâne `cada`. */
/** CADA OVALĂ: o ELIPSĂ, nu un dreptunghi cu capetele rotunjite.
 *
 *  Prima formă era o «pastilă» — `rx = h/2` pe un dreptunghi —, deci avea
 *  laturile lungi DREPTE, iar între ele se citea tot un dreptunghi. «Cada
 *  ovală should not have the rectangular outline... it should be just the oval
 *  part» (13.10.2026). O elipsă n-are nicio latură dreaptă.
 *
 *  Linia dinăuntru rămâne: e MARGINEA căzii, nu un contur în plus — aceeași
 *  citire ca la cada dreptunghiulară, unde cuva e desenată înăuntrul bordurii.
 *  Și ea e tot o elipsă, deci nu întoarce dreptunghiul pe ușa din dos. */
function CadaOvala({ w, h }: { w: number; h: number }) {
  const m = Math.min(0.055, Math.min(w, h) * 0.07);
  const el = (rx: number, ry: number, sw: number) => (
    <ellipse cx={0} cy={0} rx={rx} ry={ry} fill={FOND} stroke={LINIE}
             style={{ strokeWidth: g(sw) }} vectorEffect="non-scaling-stroke" />
  );
  return (
    <g>
      {el(w / 2, h / 2, 0.022)}
      {el(w / 2 - m, h / 2 - m, 0.018)}
      <C cx={w * 0.28} cy={0} r={0.028} sw={0.014} />
    </g>
  );
}

/** DUȘUL FĂRĂ CABINĂ: tăvița dreptunghiulară cu diagonalele întregi și
 *  panoul de sticlă pe o latură.
 *
 *  Se deosebește de cabină prin ce NU are: nicio ușă, niciun arc de deschidere
 *  — se intră direct. Diagonalele trec PESTE scurgere, dintr-un colț în
 *  celălalt (la cabină se opresc la sifon), iar banda groasă de pe latura
 *  scurtă e peretele de sticlă. */
function DusDeschis({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const b = Math.min(0.05, Math.min(w, h) * 0.06);
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.02} />
      <R x={x + b} y={y + b} w={w - 2 * b} h={h - 2 * b} rx={0.015} sw={0.014} />
      <L x1={x + b} y1={y + b} x2={x + w - b} y2={y + h - b} sw={0.014} />
      <L x1={x + w - b} y1={y + b} x2={x + b} y2={y + h - b} sw={0.014} />
      <C cx={x + b + 0.1} cy={0} r={0.035} sw={0.015} />
      {/* Panoul de sticlă prin care se intră, PE muchia dinspre cameră — adică
          ÎNĂUNTRUL cutiei piesei, nu dincolo de ea. Cutia de editare se
          socotește din `w`/`h` (v. `Panza`, mânerele mobilei), deci orice linie
          desenată peste muchie lasă mânerele în corpul piesei, și omul vede un
          box mai mic decât obiectul. Banda e chiar fâșia dintre conturul de
          afară și tăvița dinăuntru (`b`), deci rămâne lipită de muchie și la o
          piesă strânsă din mână. */}
      <R x={x} y={y} w={w} h={b} rx={0.01} sw={0.016} />
    </g>
  );
}

/** THE SLIDING-DOOR SHOWER: the tray with its diagonals and drain, and on the
 *  two room sides (+x, +y) two glass panels each, drawn closed in two tracks
 *  that overlap mid-side; the corner panels meet at the +x+y corner, the
 *  entry. The walls' sides carry no glass, nothing swings, and nothing is
 *  drawn outside the box. Turning and mirroring the piece choose the corner. */
function DusColt({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const sticla = (pe: "x" | "y") => {
    const lung = pe === "y" ? w : h;
    const start = pe === "y" ? x : y;
    const muchie = pe === "y" ? y + h : x + w;
    const panouri: Array<[number, number, number]> = [
      [start, start + lung / 2 + 0.025, 0.01],            // fixed, by the wall
      [start + lung / 2 - 0.025, start + lung, 0.03],     // sliding, by the corner
    ];
    return panouri.flatMap(([a, b, d], i) => [d, d + 0.02].map((o, j) => (
      pe === "y"
        ? <L key={`${pe}${i}${j}`} x1={a} y1={muchie - o} x2={b} y2={muchie - o} sw={0.012} />
        : <L key={`${pe}${i}${j}`} x1={muchie - o} y1={a} x2={muchie - o} y2={b} sw={0.012} />
    )));
  };
  const b = 0.05;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.02} />
      <L x1={x} y1={y} x2={x + w - b} y2={y + h - b} sw={0.014} />
      <L x1={x + w - b} y1={y} x2={x} y2={y + h - b} sw={0.014} />
      <C cx={x + (w - b) / 2} cy={y + (h - b) / 2} r={0.035} sw={0.015} />
      {sticla("y")}
      {sticla("x")}
    </g>
  );
}

/** INSULA CU CHIUVETĂ: blatul liber, cu cuva dublă tăiată în el și corpurile
 *  de dedesubt punctate — cum e desenată pe planșă. */
function InsulaCuChiuveta({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const cw = Math.min(0.62, w * 0.4);
  const ch = Math.min(0.34, h * 0.4);
  const cy = y + h * 0.6;
  const dulap = Math.min(0.44, w * 0.26);
  const punctat = (i: number, d: string) => (
    <path key={i} fill="none" stroke={LINIE} strokeDasharray="0.07 0.05"
          style={{ strokeWidth: g(0.015) }} vectorEffect="non-scaling-stroke"
          d={d} />
  );
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0.02} />
      {/* muchia tăbliei, pe lungul insulei */}
      {punctat(0, `M${x} ${y + h * 0.36}h${w}`)}
      {/* corpurile de dedesubt, la cele două capete */}
      {[0, 1].map((s) => punctat(s + 1,
        `M${x + 0.09 + s * (w - 0.18 - dulap)} ${y + h * 0.42}`
        + `h${dulap}v${h * 0.46}h${-dulap}Z`))}
      {/* chiuveta cu două compartimente: una de spălat, una de scurs */}
      <R x={-cw / 2} y={cy - ch / 2} w={cw} h={ch} rx={0.05} sw={0.019} />
      <L x1={0} y1={cy - ch / 2} x2={0} y2={cy + ch / 2} sw={0.015} />
      <C cx={-cw * 0.25} cy={cy} r={0.026} sw={0.014} />
      <C cx={cw * 0.25} cy={cy} r={0.026} sw={0.014} />
      <Baterie cx={0} y={cy - ch / 2 - 0.085} lung={0.085} />
    </g>
  );
}

/** MAȘINA DE SPĂLAT: corpul, cu hubloul. */
function Masina({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) * 0.3;
  return (
    <g>
      <R x={-w / 2} y={-h / 2} w={w} h={h} rx={0.02} />
      <C cx={0} cy={0} r={r} sw={0.018} />
      <L x1={-w / 2} y1={-h / 2 + 0.1} x2={w / 2} y2={-h / 2 + 0.1} sw={0.014} />
      {/* Sigla STĂ ÎN OBLOU: acolo e golul desenului, iar cercul rămâne ce
          spune că e o mașină. Lățimea de scris e coarda lui, nu diametrul. */}
      <Sigla text="MSR" lat={r * 1.6} />
    </g>
  );
}

/** SIGLA UNEI PIESE — «MSV», «MSR».
 *
 *  Cerut de arhitect (13.10.2026): «să scrie MSV pe mașina de spălat vase și
 *  MSR pe mașina de spălat rufe». Așa se citesc pe planșele lui: cele două
 *  mașini au aceeași cutie de 60 × 60 și, fără literele de pe ele, nu se
 *  deosebesc dintr-o privire — iar una stă în bucătărie și cealaltă în baie.
 *
 *  CORPUL IESE DIN LĂȚIMEA DATĂ, nu dintr-o cifră fixă: simbolul se desenează
 *  în metri, deci un corp scris o dată ar fi fost uriaș pe o piesă mică.
 *  Literele unei sans la 0,62 din corp, ca în restul desenului. */
/** E PIESA OGLINDITĂ, și pe ce axe?
 *
 *  Desenul unei piese stă într-un grup cu `scale(-1, 1)` când e oglindită
 *  (v. `Desen`), iar asta n-avea urmări cât timp piesele erau numai linii și
 *  hașuri — chiar așa scria și nota de-acolo. De când unele poartă LITERE
 *  («MSV» pe mașina de vase, «MSR» pe cea de rufe, 13.10.2026), scara negativă
 *  le scrie de-a-ndoaselea. Cerut de arhitect (21.10.2026): textul nu se
 *  oglindește odată cu piesa.
 *
 *  UN CONTEXT, NU UN PROP DUS DIN MÂNĂ ÎN MÂNĂ: sigla stă adânc, în corpul
 *  fiecărei piese care o poartă, iar un parametru ar fi trebuit trecut prin
 *  toate desenele dintre ele — inclusiv prin cele care n-au text și pe care
 *  nu-i privește. */
export const Oglinda = createContext<{ x: boolean; y: boolean; deg: number }>(
  { x: false, y: false, deg: 0 });

function Sigla({ text, lat, y = 0 }: { text: string; lat: number; y?: number }) {
  const corp = Math.min(0.15, lat / (text.length * 0.62));
  const o = useContext(Oglinda);
  // ÎNTOARSĂ ÎNAPOI, în jurul locului ei: se mută întâi în punctul unde stă
  // sigla, iar contra-oglindirea se face ACOLO. Pusă pe `<text>` de-a dreptul,
  // scara ar fi lucrat în jurul originii piesei și ar fi aruncat litera de
  // cealaltă parte a ei.
  // ÎNTOARSĂ ÎNAPOI DIN AMÂNDOUĂ: piesa o duce prin `rotate(deg) scale(s)`
  // (v. `Desen`), deci litera se îndreaptă aplicând inversele în ordine
  // răsturnată — `scale(s)` (care e propria lui inversă) și apoi `rotate(-deg)`.
  // Așa partea liniară a transformării adunate iese chiar identitatea, oricât
  // ar fi întoarsă piesa. Cerut de arhitect (21.10.2026): «unde apare text pe
  // obiectele de mobilier (ex: MSR, MSV) el nu trebuie să se rotească atunci
  // când rotesc obiectul».
  const contra = (o.x || o.y ? ` scale(${o.x ? -1 : 1} ${o.y ? -1 : 1})` : "")
    + (o.deg ? ` rotate(${-o.deg})` : "");
  return (
    <g transform={`translate(0 ${y})${contra}`}>
      <text x={0} y={0} textAnchor="middle" dominantBaseline="central"
            fontSize={corp} fill={LINIE} fontWeight={600}
            style={{ userSelect: "none" }}>{text}</text>
    </g>
  );
}

/** PLANTA: un cerc dublu, ca un ghiveci văzut de sus. */
function Planta({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) / 2;
  return (
    <g>
      <C cx={0} cy={0} r={r} sw={0.02} />
      <C cx={0} cy={0} r={r * 0.52} sw={0.016} />
    </g>
  );
}

/** VEIOZA: cerc mic cu cruce — abajurul văzut de sus. Desenată întreruptă:
 *  stă pe noptieră, deasupra tăieturii. */
function Veioza({ w, h }: { w: number; h: number }) {
  const r = Math.min(w, h) / 2;
  return (
    <g>
      <circle cx={0} cy={0} r={r} fill={FOND} stroke={LINIE}
              style={{ strokeWidth: g(0.018) }} strokeDasharray="0.05 0.04"
              vectorEffect="non-scaling-stroke" />
      <L x1={-r * 0.7} y1={0} x2={r * 0.7} y2={0} sw={0.014} />
      <L x1={0} y1={-r * 0.7} x2={0} y2={r * 0.7} sw={0.014} />
    </g>
  );
}

/** O PIESĂ SINGURĂ, centrată în dreptunghiul ei.
 *
 *  Întoarce `null` pentru un fel necunoscut — la fel ca `piesaPlan`, ca un
 *  document scris de o versiune mai nouă să nu strice desenul, ci doar să nu
 *  arate piesa aia. */
/** CONTURUL UNEI PIESE ADUSE DIN CAD, întins în cutia ei.
 *
 *  Conturul vine în mărimea lui adevărată, din bloc; cutia piesei o poate
 *  schimba cine o așază. Se scalează, deci, pe amândouă axele — o canapea
 *  lățită rămâne canapea, chiar dacă nu mai e chiar desenul din bibliotecă.
 *  Se desenează centrat, ca toate celelalte piese (v. `piesaSingura`). */
function Contur({ contur, w, h }: { contur: number[][][]; w: number; h: number }) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const c of contur) for (const [x, y] of c) {
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const dw = x1 - x0, dh = y1 - y0;
  if (!(dw > 0) || !(dh > 0)) return null;
  const kx = w / dw, ky = h / dh;
  return (
    <g>
      {contur.map((c, i) => (
        <path key={i} fill="none" stroke={LINIE} style={{ strokeWidth: g(0.018) }}
              vectorEffect="non-scaling-stroke"
              d={"M" + c.map(([x, y]) =>
                `${((x - x0) * kx - w / 2).toFixed(4)} `
                + `${((y - y0) * ky - h / 2).toFixed(4)}`).join("L")} />
      ))}
    </g>
  );
}

/** GHENA: dreptunghiul puțului, hașurat pieziș ca pe planșă. */
function Ghena({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const pas = 0.08;
  const linii: ReactNode[] = [];
  // Hașura merge de la colțul stânga-sus spre dreapta-jos, tăiată de cutie.
  for (let d = -h; d < w; d += pas) {
    const x1 = Math.max(x + d, x);
    const y1 = y + Math.max(-d, 0);
    const x2 = Math.min(x + d + h, x + w);
    const y2 = y + Math.min(h, w - d);
    if (x2 - x1 > 1e-6) {
      linii.push(<L key={d} x1={x1} y1={y1} x2={x2} y2={y2} sw={0.012} />);
    }
  }
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0} sw={0.03} />
      {linii}
    </g>
  );
}

/** `optiuni` e pentru SCĂRI și nu atinge restul catalogului.
 *
 *  `hNivel` — cât are de urcat scara, adică de câte contratrepte e nevoie
 *  (v. `scara.ts`). Lipsă = 2,80, nivelul obișnuit. `traseu` — semicercul
 *  punctat de evacuare, adnotare de planșă, cerut separat de cine desenează. */
export function piesaSingura(kind: string, w: number, h: number,
                             optiuni?: { hNivel?: number; traseu?: boolean },
): ReactNode {
  // SIMBOLUL DIN PLANȘĂ BATE DESENUL DE MÂNĂ, când există unul (v. `Simbol`).
  // Desenele de mână rămân pentru tot restul — și rămân ca plasă: un simbol
  // scos greșit din PDF se vede, se scoate din tabel, și piesa se întoarce la
  // ce era.
  const simbol = SIMBOL_PE_FEL[kind];
  if (simbol) {
    const c: SimbolulFelului = typeof simbol === "string" ? { nume: simbol } : simbol;
    return <Simbol nume={c.nume} rot={c.rot} margine={c.margine} corp={c.corp}
                   w={w} h={h} />;
  }
  switch (kind) {
    case "scara":
      return <Scara w={w} h={h} hNivel={optiuni?.hNivel} />;
    case "scaraU":
      return <ScaraU w={w} h={h} hNivel={optiuni?.hNivel}
                     traseu={optiuni?.traseu} />;
    case "lift": return <Lift w={w} h={h} />;
    case "ghena": return <Ghena w={w} h={h} />;
    case "canapea": return <Canapea x={-w / 2} y={-h / 2} w={w} h={h} />;
    case "fotoliu": return <Fotoliu x={-w / 2} y={-h / 2} w={w} h={h} />;
    case "masa": return <Masa w={w} h={h} />;
    case "masa_scaune": return <MasaCuScaune w={w} h={h} />;
    case "masa_rotunda": return <MasaRotunda cx={0} cy={0} r={Math.min(w, h) / 2} />;
    case "scaun": return <Scaun w={w} h={h} />;
    case "pat": case "pat_o_persoana": return <Pat w={w} h={h} />;
    case "tv": return <Tv x={-w / 2} y={-h / 2} w={w} />;
    // Piesa umple cutia ei: tăvița e chiar dreptunghiul scris în document,
    // nu un pătrat mai mic decalat în sus cum o desenează funcțiunea de cameră.
    case "dus": { const s = Math.min(w, h); return <CabinaDus x={-s / 2} y={-s / 2} s={s} />; }
    case "wc": return <Vas x={-w / 2} y={-h / 2} w={w} h={h} />;
    case "aragaz": return <Plita w={w} h={h} />;
    case "frigider": return <Frigider w={w} h={h} />;
    case "canapea_colt": return <CanapeaColt w={w} h={h} />;
    case "masuta_cafea": case "masuta": case "comoda_tv":
    case "corp_bucatarie": case "corp_sertare":
      return <Corp w={w} h={h} fel={kind} />;
    case "noptiera": return <Noptiera w={w} h={h} />;
    case "corp_suspendat": return <Suspendat w={w} h={h} />;
    case "hota": return <Suspendat w={w} h={h} hota />;
    case "raft": case "biblioteca": return <Raft w={w} h={h} />;
    case "lavoar": return <Lavoar w={w} h={h} />;
    case "lavoar_dublu": return <LavoarDublu w={w} h={h} />;
    case "cada_ovala": return <CadaOvala w={w} h={h} />;
    case "dus_deschis": return <DusDeschis w={w} h={h} />;
    case "dus_colt": return <DusColt w={w} h={h} />;
    case "insula_chiuveta": return <InsulaCuChiuveta w={w} h={h} />;
    case "sezlong": return <Sezlong w={w} h={h} />;
    case "fotoliu_exterior": return <FotoliuExterior w={w} h={h} />;
    case "canapea_exterior": return <CanapeaExterior w={w} h={h} />;
    case "masa_exterior": return <MasaExterior w={w} h={h} />;
    case "masuta_exterior": return <MasutaExterior w={w} h={h} />;
    case "umbrela": return <Umbrela w={w} h={h} />;
    case "planta_terasa": return <PlantaTerasa w={w} h={h} />;
    case "masina_spalat": return <Masina w={w} h={h} />;
    case "masina_vase": return <Corp w={w} h={h} fel={kind} />;
    case "wc_suspendat":
      return <Vas x={-w / 2} y={-h / 2} w={w} h={h} rezervor={false} />;
    case "covor": return <Covor w={w} h={h} />;
    case "covor_rotund": return <Covor w={w} h={h} rotund />;
    case "planta": case "planta_mica": return <Planta w={w} h={h} />;
    case "veioza": return <Veioza w={w} h={h} />;
    case "dulap": case "birou":
    case "chiuveta": case "cada": case "insula_bucatarie":
      return <Corp w={w} h={h} fel={kind} />;
    default: {
      // PIESELE ADUSE DIN CAD cad aici: n-au caz scris, dar au desen.
      const f = PE_FEL.get(kind);
      return f?.contur ? <Contur contur={f.contur} w={w} h={h} /> : null;
    }
  }
}

// --------------------------------------------------- scara și puțul de lift ---
//
// MĂSURILE VIN DIN PLANȘĂ, nu dintr-un catalog de mobilă: golul de lift e 160
// lățime pe 180 adâncime, cu pereți de 30 în jur, iar golul de desfumare al
// scării 160 x 145 (măsurat de Cătălin pe modulele noastre, 09.09.2026). Scara
// diferă de la modul la modul, deci ce se pune e o mărime de pornire, aleasă cât
// cea mai obișnuită dintre ele; restul se face trăgând de colțuri.

/** PUȚUL DE LIFT: cutia, cu diagonalele care spun «gol», și ușile pe o latură. */
function Lift({ w, h }: { w: number; h: number }) {
  const x = -w / 2, y = -h / 2;
  const m = 0.06;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0} />
      <R x={x + m} y={y + m} w={w - 2 * m} h={h - 2 * m} rx={0} />
      {/* diagonalele golului */}
      <L x1={x + m} y1={y + m} x2={x + w - m} y2={y + h - m} />
      <L x1={x + w - m} y1={y + m} x2={x + m} y2={y + h - m} />
      {/* ușile, pe latura de jos: două canaturi care se retrag în perete */}
      <R x={x + w * 0.1} y={y + h - m} w={w * 0.35} h={m} rx={0} />
      <R x={x + w * 0.55} y={y + h - m} w={w * 0.35} h={m} rx={0} />
    </g>
  );
}

/** SCARA DREAPTĂ: rampa, treptele ei, planul tăiat și linia de urcare.
 *
 *  CÂTE TREPTE ȘI DE CE. Nu „cât încape la 28 cm", ci câte urcă nivelul — v.
 *  `scara.ts`. O scară trasă mai scurtă are trepte mai înghesuite, nu mai
 *  puține; una mai lungă le are mai comode. Numărul se schimbă doar când se
 *  schimbă înălțimea nivelului, fiindcă doar atunci se schimbă ce are de urcat.
 *
 *  PLANUL E TĂIAT, ca orice plan de arhitectură: până la planul de secționare
 *  treptele se văd pline, deasupra lui punctate, iar între ele stă linia de
 *  rupere. Desenul de până acum le trăgea pe toate la fel, deci o scară arăta
 *  identic la parter și la ultimul nivel. */
function Scara({ w, h, hNivel = 2.8 }: {
  w: number; h: number; hNivel?: number;
}) {
  const x = -w / 2, y = -h / 2;
  const s = scaraDin("scara", w, h, hNivel);
  const n = Math.max(1, s.contratrepte - 1);
  if (n < 3) return <R x={x} y={y} w={w} h={h} rx={0} />;
  const pas = h / n;
  // Tăietura cade pe la jumătatea rampei — acolo o pune și Revit, la 1,20 m
  // deasupra pardoselii, adică pe la a șaptea treaptă a unei scări obișnuite.
  const taiat = Math.max(1, Math.min(n - 1, Math.round(1.2 / s.pas)));
  const yT = (i: number) => y + pas * i;
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0} />
      {Array.from({ length: n - 1 }, (_, i) => (
        <L key={i} x1={x} y1={yT(i + 1)} x2={x + w} y2={yT(i + 1)}
           punctat={n - 1 - i > taiat} />
      ))}
      <Rupere x={x} w={w} y={yT(n - taiat)} />
      <Urcare x={x} w={w} y1={y + h} y2={y} />
    </g>
  );
}

/** SCARA ÎN U: două rampe cu podestul între ele — forma din video.
 *
 *  Contratreptele se împart pe rampe (8 + 8 la cele 16 ale unui nivel de 2,86),
 *  iar urcarea merge pe una, se întoarce pe podest și continuă pe cealaltă. */
function ScaraU({ w, h, hNivel = 2.8, traseu = false }: {
  w: number; h: number; hNivel?: number; traseu?: boolean;
}) {
  const x = -w / 2, y = -h / 2;
  const s = scaraDin("scaraU", w, h, hNivel);
  const lat = s.latRampa, och = Math.max(0.04, w - 2 * lat);
  const xU = x + lat + och;                 // rampa pe care se URCĂ
  const yPod = y + s.podest;                // podestul, sus
  const yJos = y + h;
  const n = Math.max(1, s.rampe[0] - 1);    // treptele desenate pe o rampă
  const pas = s.lungRampa / Math.max(1, n);
  const yT = (i: number) => yPod + pas * i;
  const taiat = Math.max(1, Math.min(n - 1, Math.round(1.2 / s.pas)));
  const rand = (x0: number, punctTot: boolean) =>
    Array.from({ length: n - 1 }, (_, i) => (
      <L key={`${x0}-${i}`} x1={x0} y1={yT(i + 1)} x2={x0 + lat} y2={yT(i + 1)}
         punctat={punctTot || n - 1 - i > taiat} />
    ));
  return (
    <g>
      <R x={x} y={y} w={w} h={h} rx={0} />
      {/* ochiul dintre rampe */}
      <L x1={x + lat} y1={yPod} x2={x + lat} y2={yJos} />
      <L x1={xU} y1={yPod} x2={xU} y2={yJos} />
      {/* podestul */}
      <L x1={x} y1={yPod} x2={x + w} y2={yPod} sw={0.025} />
      {rand(xU, false)}
      {rand(x, true)}
      <Rupere x={xU} w={lat} y={yT(n - taiat)} />
      <Urcare x={xU} w={lat} y1={yJos} y2={yPod} />
      {traseu && <TraseuEvacuare x={x} w={w} y={yPod} inalt={s.podest} />}
    </g>
  );
}

/** LINIA DE RUPERE: dubla diagonală care spune unde s-a tăiat rampa. */
function Rupere({ x, w, y }: { x: number; w: number; y: number }) {
  // Panta: cât un sfert din lățimea rampei, nu cât toată — o diagonală lungă
  // taie patru trepte și se citește ca o rampă înclinată, nu ca o rupere.
  const d = Math.min(0.2, w * 0.22), e = Math.min(0.09, w * 0.08);
  return (
    <g>
      {[0, e].map((k, i) => (
        <L key={i} x1={x - 0.02} y1={y + d + k} x2={x + w + 0.02} y2={y - d + k}
           sw={0.022} />
      ))}
    </g>
  );
}

/** LINIA DE URCARE: bulina de pornire, linia pe mijlocul rampei, săgeata sus. */
function Urcare({ x, w, y1, y2 }: { x: number; w: number; y1: number; y2: number }) {
  const m = x + w / 2, v = Math.sign(y2 - y1) || -1;
  // Bulina stă ÎN rampă, nu pe muchia ei: pe muchie se citea ca un punct scăpat
  // lângă scară, nu ca începutul urcării.
  const jos = y1 + v * 0.09;
  const cap = y2 - v * 0.05, a = Math.min(0.15, w * 0.16);
  return (
    <g>
      <circle cx={m} cy={jos} r={0.05} fill={LINIE} />
      <line x1={m} y1={jos} x2={m} y2={cap} stroke={LINIE}
            style={{ strokeWidth: g(0.03) }} vectorEffect="non-scaling-stroke" />
      <path d={`M ${m - a} ${cap - v * 0.26} L ${m} ${cap} L ${m + a} ${cap - v * 0.26}`}
            fill="none" stroke={LINIE} style={{ strokeWidth: g(0.03) }}
            vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/** TRASEUL DE EVACUARE: aria de întoarcere de pe PODEST.
 *
 *  Semnul cerut de Bianca pe 05.10.2026, după planșa braziliană trimisă. NU e
 *  un cerc în jurul scării — e aria în care se întorc oamenii: cine a urcat o
 *  rampă mătură podestul ca să ajungă la cealaltă.
 *
 *  SE ÎNTINDE DE LA O MARGINE A SCĂRII LA CEALALTĂ și e exact cât podestul de
 *  înalt, adică îl umple. De-aia nu e cerc, ci jumătate de ELIPSĂ: raza pe
 *  orizontală e jumătate din lățimea scării, cea pe verticală e adâncimea
 *  podestului, iar cele două nu sunt egale decât din întâmplare. Desenat ca
 *  semicerc, pe o scară lată ar fi ieșit din podest, iar pe una îngustă ar fi
 *  lăsat jumătate de podest gol.
 *
 *  Deci numai la scara în U: o scară dreaptă n-are podest și n-are ce lega.
 *
 *  E adnotare de planșă, nu construcție — nu intră în nicio arie. */
function TraseuEvacuare({ x, w, y, inalt }: {
  /** Marginea din stânga a scării și lățimea ei — capetele arcului. */
  x: number; w: number;
  /** Muchia podestului dinspre rampe: pe ea stă deschiderea arcului. */
  y: number;
  /** Adâncimea podestului — cât urcă burta arcului în el. */
  inalt: number;
}) {
  const rx = w / 2, ry = inalt;
  if (rx <= 0.01 || ry <= 0.01) return null;
  return (
    <path d={`M ${x} ${y} A ${rx} ${ry} 0 0 1 ${x + w} ${y}`} fill="none"
          stroke={LINIE} strokeDasharray={`${g(0.13)} ${g(0.085)}`} opacity={0.75}
          style={{ strokeWidth: g(0.028) }} vectorEffect="non-scaling-stroke" />
  );
}
